import type { Group } from 'three';
import {
  ACESFilmicToneMapping,
  Quaternion,
  Scene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from 'three';
import { BALANCE } from '../config/balance';
import { difficultyProfile } from '../config/difficulty';
import { EventBus } from './EventBus';
import { GameSession } from './GameSession';
import { GameLoop } from './GameLoop';
import type { GameEvents, GamePhase } from './GameEvents';
import { Input } from './Input';
import { Settings } from './Settings';
import { HighScores } from './HighScores';
import { AssetManager, hideBootScreen, showContextLostError } from './Assets';
import type { InputFrame } from './Input';
import type { Action as InputAction } from '../config/controls';
import { Debug } from './Debug';
import { Robot } from '../player/Robot';
import type { LocomotionState } from '../player/LocomotionStateMachine';
import { CameraRig } from '../camera/CameraRig';
import { CollisionWorld } from '../world/CollisionWorld';
import { buildLevel, type BuiltLevel } from '../world/LevelBuilder';
import type { Surface } from '../world/Surface';
import { clamp } from '../utils/math';
import { validateLevel } from '../world/LevelDefinition';
import { LEVEL_CATALOG, getLevel } from '../levels/catalog';
import { DroneController } from '../enemies/DroneController';
import type { Drone } from '../enemies/Drone';
import { Weapon } from '../combat/Weapon';
import { hitscan } from '../combat/Hitscan';
import { GrappleHook } from '../combat/GrappleHook';
import { Melee } from '../combat/Melee';
import { NetGun } from '../combat/NetGun';
import { BulletTime } from './BulletTime';
import { Rng } from './Rng';
import { clampShotOrigin, leanLock, resolveThirdPersonAim, type AimSolution } from '../combat/Aim';
import { hullBreachesOnRicochet } from '../combat/Ricochet';
import type { Projectile } from '../combat/Projectiles';
import { Score } from '../combat/Score';
import { HeadTracker } from '../perception/HeadTracker';
import { HeadTiltWatcher } from '../perception/HeadTilt';
import { bodyShiftFromOffset } from '../perception/HeadShift';
import { AudioManager } from '../audio/AudioManager';
import { Hud, bearingFrom } from '../ui/Hud';
import { Menu, type SettingsView } from '../ui/Menu';
import { MapEditor } from '../ui/MapEditor';
import { CalibrationStage } from '../ui/CalibrationStage';
import { ScreenCalibrationWizard } from '../ui/ScreenCalibrationWizard';
import {
  calibratedViewScale,
  defaultViewerReach,
  sanitizeScreenCalibration,
  viewerReachFor,
  viewingDistanceMetres,
  type ScreenCalibrationData,
  type ViewerReach,
} from '../perception/ScreenCalibration';
import { MapStore } from '../builder/MapStore';
import {
  compileMapForGame,
  componentAt,
  createMap,
  type MapDocument,
} from '../builder/MapDocument';
import { BuildController, BUILD_REACH } from '../builder/BuildController';
import { BuildHologram } from '../graphics/BuildHologram';
import { DroneRouteView } from '../graphics/DroneRouteView';
import { ModelLibrary } from '../graphics/ModelLibrary';
import { buildObstacles, rebuildCover } from '../graphics/LevelMeshBuilder';
import { BulletTimeOverlay } from '../graphics/BulletTimeOverlay';
import type { BoltVisual } from '../graphics/BoltView';
import { logWarn } from './Debug';
import { DebugOverlay } from '../ui/DebugOverlay';
import { TouchControls } from '../ui/TouchControls';
import { detectTouchDevice } from './Device';
import { RobotView } from '../graphics/RobotView';
import { DroneView } from '../graphics/DroneView';
import { BoltView } from '../graphics/BoltView';
import { Effects } from '../graphics/Effects';
import { GrappleView } from '../graphics/GrappleView';
import { buildLevelMeshes, buildLighting, disposeLevelMeshes } from '../graphics/LevelMeshBuilder';

const scratchPosition = new Vector3();
const scratchForward = new Vector3();
const scratchMuzzle = new Vector3();
const scratchProjected = new Vector3();
const scratchCameraPosition = new Vector3();
const scratchCameraForward = new Vector3();
const scratchHeadPosition = new Vector3();
const scratchHeadDirection = new Vector3();
/** The body's attach point, used to keep a shot origin inside the room. */
const scratchAimAnchor = new Vector3();
const scratchJump = new Vector3();
const scratchAssist = new Vector3();
const scratchThreat = new Vector3();
const scratchPull = new Vector3();
const scratchNoise = new Vector3();
const scratchClawDirection = new Vector3();
const scratchClawTip = new Vector3();
const scratchClawInverse = new Quaternion();
/** The head rig's viewer offset, handed to the robot view every frame. */
const scratchHeadOffset = new Vector3();
/** Scratch local surface coordinates for the hull-breach spot test. */
const scratchLocal = { u: 0, v: 0 };
/** The empty threat list, so clearing the attacker ring allocates nothing. */
const NO_THREATS: number[] = [];
/** Scratch for the bolt pop-out: head/tail projections and a travel direction. */
const scratchBoltHead = { x: 0, y: 0 };
const scratchBoltDirection = new Vector3();
/** Bolt minus eye, for the view-axis depth that decides when one has gone by. */
const scratchBoltOffset = new Vector3();
/** Pool ceiling for the pop-out bolts, matching `BoltView`. */
const BOLT_POP_POOL = 24;
const scratchSwayWorld = new Vector3();
const scratchLeanRight = new Vector3();
const scratchLeanUp = new Vector3();

/**
 * The command handed to the robot every simulation step. It is allocated once
 * and mutated in place so the hot path never allocates
 * (see `docs/ARCHITECTURE.md` §12).
 */
const robotCommand = {
  move: { x: 0, y: 0 },
  /** Tracked head lean, used for the first-person sway and vertical dodge. */
  headShift: { x: 0, y: 0, z: 0 },
  /** Bound on the lean's swing; bullet time raises it. */
  swayRange: BALANCE.headTracking.swayRange as number,
  jumpPressed: false,
  crouchHeld: false,
  sneakHeld: false,
  grabHeld: false,
  viewForward: new Vector3(),
  viewRight: new Vector3(),
  jumpDirection: new Vector3(),
  /** Body time scale; the head-led dodge ignores it (see `Robot.update`). */
  timeScale: 1,
};

export interface GameOptions {
  /**
   * How to build the renderer.
   *
   * The default creates a `WebGLRenderer`, which needs a real WebGL context.
   * Tests drive the game without one by supplying a stub.
   */
  createRenderer?: (canvas: HTMLCanvasElement) => WebGLRenderer;
  /**
   * Run the head-tracking pipeline on generated motion instead of a webcam.
   *
   * The `?headtrackdemo=1` query switch does the same thing in the browser.
   */
  syntheticHeadTracking?: boolean;
  /** Start on a specific level instead of the `?level=` query switch. */
  level?: string;
  /**
   * Force the touch interface on or off.
   *
   * The `?touch=1` / `?touch=0` query switches do the same thing in the
   * browser; tests use this so they do not depend on device detection.
   */
  touch?: boolean;
}

/**
 * The renderer the game uses in the browser.
 *
 * Creating the context can fail outright — most often because the browser has
 * blocked new WebGL contexts for the page after previous ones were lost. The
 * raw Three.js error is opaque, so it is replaced with something a player can
 * act on; the boot overlay offers a reload.
 */
function createWebGLRenderer(canvas: HTMLCanvasElement): WebGLRenderer {
  try {
    return new WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
  } catch (error) {
    throw new Error(
      'WebGL could not start — the browser blocked a new graphics context. ' +
        'This can happen after the tab has been open a long time or with several 3D tabs open. ' +
        'Reload the page to try again.',
      { cause: error },
    );
  }
}

/** The snapshot of the game state exposed to the debug overlay and the QA hooks. */
export interface GameDebugState {
  phase: GamePhase;
  robotState: string;
  surface: string;
  droneCount: number;
  score: number;
  position: [number, number, number];
  fps: number;
  velocity: number;
  cameraMode: string;
  weapon: 'ar' | 'net';
  bulletTime: boolean;
  grapple: string;
  audio: {
    context: AudioContextState | 'none';
    hums: number;
    counts: Record<string, number>;
  };
}

/**
 * Wires every system together and owns the game phases.
 */
export class Game {
  private readonly bus = new EventBus<GameEvents>();
  private readonly session = new GameSession();
  private readonly settings = new Settings();
  private readonly highScores = new HighScores();
  private readonly input = new Input();
  private readonly audio = new AudioManager();
  private readonly headTracker: HeadTracker;
  private readonly assets = new AssetManager();
  private readonly score = new Score(this.bus);

  private readonly scene = new Scene();
  private disposed = false;
  private readonly renderer: WebGLRenderer;
  private readonly loop: GameLoop;
  private readonly cameraRig: CameraRig;
  private readonly robot: Robot;
  private readonly collision: CollisionWorld;
  private readonly drones: DroneController;
  private readonly weapon: Weapon;
  /** The dice for a bounced shot's hull-breach roll; level-seeded. */
  private readonly breachRng: Rng;
  private readonly grapple: GrappleHook;
  private readonly netGun = new NetGun();
  private readonly melee = new Melee();
  private readonly bulletTime = new BulletTime();
  private weaponMode: 'ar' | 'net' = 'ar';
  /** True while bullet time pulled the player out of third person. */
  private bulletTimeForcedFirst = false;
  private bulletTimeWasActive = false;
  /** The level is built once; every system shares the same room instance. */
  private readonly built: BuiltLevel;

  private readonly robotView = new RobotView();
  private readonly droneViews = new Map<string, DroneView>();
  private readonly boltView = new BoltView(this.scene);
  /** Bolts already flashed; a WeakSet lets fired projectiles be collected. */
  private readonly seenBolts = new WeakSet<Projectile>();
  private effects: Effects | null = null;
  /** The drawn hook and line; built with the effects, hidden with them. */
  private grappleView: GrappleView | null = null;
  /** Level meshes and lights, kept so they can be released on dispose. */
  private levelMeshes: ReturnType<typeof buildLevelMeshes> | null = null;
  private levelLights: ReturnType<typeof buildLighting> | null = null;

  private readonly hud: Hud;
  private readonly menu: Menu;
  private readonly debugOverlay: DebugOverlay;
  /** Draft storage for the map builder. */
  private readonly mapStore = new MapStore();
  private readonly mapEditor: MapEditor;
  /** The boot calibration stage: webcam preview plus the blurred head map. */
  private readonly calibrationStage: CalibrationStage;
  /** The screen & distance calibration wizard, opened from the stage. */
  private readonly screenWizard: ScreenCalibrationWizard;
  /** The viewer's usable travel, from the screen calibration. */
  private viewerReach: ViewerReach = defaultViewerReach();
  /** True when this boot loaded a map from the builder rather than the catalog. */
  private readonly customRun: boolean;
  /** True while the run is a build session rather than a firefight. */
  private buildMode = false;
  /** The 3D room builder's placement logic and its scanner outline. */
  private readonly buildController: BuildController;
  private readonly buildHologram = new BuildHologram();
  /** The drawn patrol loops and monitoring areas of the map's drones. */
  private readonly droneRouteView = new DroneRouteView();
  /** CC0 prop models, shared by the level and the scanner's previews. */
  private readonly modelLibrary = new ModelLibrary();
  /** The meshes for whatever the scanner has placed, rebuilt as it changes. */
  private buildAssets: Group | null = null;
  private readonly touchControls: TouchControls;
  /** True when the device looks like a phone or tablet. */
  private touchDetected: boolean;
  /** `?touch=1` / `?touch=0` or the test option; null means detect. */
  private readonly touchOverride: boolean | null;

  private audioEnabled = true;
  /** The phase the effects were last applied for. */
  private appliedPhase: GamePhase = 'menu';
  private elapsed = 0;
  private lastInput: InputFrame | null = null;
  private lastFrameTime = performance.now();
  private fps = 0;
  private readonly destroyedDrones = new Set<string>();
  /** Reused between frames so the radar does not allocate. */
  private readonly livingDronePositions: Vector3[] = [];
  /** Reused scratch: the bearings of the drones firing this frame. */
  private readonly threatBearings: number[] = [];
  /** Reused scratch: the screen-anchored pop-out bolts this frame. */
  private readonly boltPopVisuals: BoltVisual[] = [];
  /**
   * A fixed pool of visual entries so the pop-out path allocates nothing after
   * boot; each frame only the used prefix is handed to the overlay.
   */
  private readonly boltPopPool: BoltVisual[] = Array.from({ length: BOLT_POP_POOL }, () => ({
    position: new Vector3(),
    velocity: new Vector3(),
    scale: 1,
  }));
  /** The bullet-time focus frame and the pop-out bolts drawn over it. */
  private readonly bulletTimeOverlay = new BulletTimeOverlay();
  /** `?noassist`: drop the near-miss assist, for bisecting a dodge feel issue. */
  private readonly assistDisabled: boolean;
  /** The origin, direction and target of the next shot. */
  private readonly aimOrigin = new Vector3();
  private readonly aimDirection = new Vector3();
  private readonly aimPoint = new Vector3();
  /** Reused by the third-person resolver so the per-frame aim allocates nothing. */
  private readonly aimSolution: AimSolution = {
    origin: this.aimOrigin,
    direction: this.aimDirection,
    point: this.aimPoint,
  };
  private renderEnabled = true;
  /** Grace after the last valid head pose before the viewer and dodge reset. */
  private headTrackingLossTimer = 0;
  /** Turns a held head roll into the "settle onto this wall" gesture. */
  private readonly headTilt = new HeadTiltWatcher();
  /** True while the menu's camera test is showing its live preview. */
  private cameraTestActive = false;
  private stepDistance = 0;
  private moving = false;
  private contextLost = false;
  private onContextLost: (event: Event) => void = () => undefined;
  private onContextRestored: () => void = () => undefined;
  private onVisibilityChange: () => void = () => undefined;
  private onCanvasMouseDown: (event: MouseEvent) => void = () => undefined;
  private onPointerLockError: () => void = () => undefined;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly uiRoot: HTMLElement,
    /** Overridable so the game can be driven without a WebGL context. */
    options: GameOptions = {},
  ) {
    const flags = new URLSearchParams(window.location.search);
    this.touchDetected = detectTouchDevice();
    const forcedTouch = flags.get('touch');
    this.touchOverride =
      options.touch ?? (forcedTouch === '1' ? true : forcedTouch === '0' ? false : null);
    const touch = this.touchActive();
    // A phone holds the camera much closer than a laptop, so the raw landmark
    // offsets arrive about twice as strong. Only a fresh install gets the
    // gentler preset; a stored choice is the player's own calibration.
    if (touch && !this.settings.hasStoredSettings) this.settings.set('headTrackingSensitivity', 1);

    this.renderer = (options.createRenderer ?? createWebGLRenderer)(canvas);
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, touch ? BALANCE.touch.pixelRatio : 2),
    );
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;

    // `?level=custom` runs the map the builder last parked in session storage,
    // which is how a test run and the 3D build mode hand a map to the game. A
    // stale or hand-edited map falls back to the catalog rather than stopping
    // the boot: `compileMapForGame` refuses anything that would not validate.
    const wantsCustom = flags.get('level') === 'custom';
    const staged = wantsCustom ? this.mapStore.staged() : null;
    const customLevel = staged ? compileMapForGame(staged) : null;
    if (wantsCustom && !customLevel) {
      logWarn('custom level: no valid map was staged; using the first catalog level');
    }
    this.customRun = customLevel !== null;
    // `?build=1` opens the room builder on the staged map instead of playing it.
    this.buildMode = flags.has('build') && customLevel !== null && staged !== null;
    const definition = customLevel ?? getLevel(options.level ?? flags.get('level'));
    const validation = validateLevel(definition);
    if (!validation.ok) {
      throw new Error(`Invalid level definition:\n${validation.errors.join('\n')}`);
    }

    const built = buildLevel(definition);
    this.built = built;
    this.collision = new CollisionWorld(built.room);
    this.robot = new Robot(built.room, this.collision);
    this.cameraRig = new CameraRig(this.collision, this.settings.get('fov'));
    this.drones = new DroneController(built.room, this.collision);
    // The scanner edits the map that was staged for this boot; without one it
    // holds a blank document and is never entered.
    this.buildController = new BuildController(this.collision, built.room, staged ?? createMap());
    // Difficulty is a property of the deck: the drones this level spawns use
    // its tier's burst cadence, lead and spread.
    this.drones.setProfile(difficultyProfile(definition.difficulty));
    // The rifle's ricochet roll and the hull-breach roll each get their own
    // level-seeded stream, so a replay rolls exactly the same shots.
    this.weapon = new Weapon(this.bus, new Rng((built.definition.seed ^ 0x9e3779b9) >>> 0));
    this.breachRng = new Rng((built.definition.seed ^ 0x85ebca6b) >>> 0);
    this.grapple = new GrappleHook(this.collision);

    // Debug switches used by the automated smoke test to bisect rendering
    // problems: ?noeffects, ?nodrones, ?nolights, ?norender, ?norobot,
    // ?nochamber and ?noaudio (skip the audio graph entirely).
    this.audioEnabled = !flags.has('noaudio');
    // Diagnostic overrides for the head-tracking lean, so a feel regression can
    // be bisected on a live page without a rebuild. `?leanlegacy` restores the
    // whole shipped lean pipeline at once, including the old One Euro smoother;
    // `?nosmooth` runs the pose raw.
    const leanLegacy = flags.has('leanlegacy');
    const leanNoSmoothing = leanLegacy || flags.has('nosmooth');
    this.assistDisabled = flags.has('noassist');
    this.headTracker = new HeadTracker({
      synthetic: options.syntheticHeadTracking ?? flags.get('headtrackdemo') === '1',
      maxRate: touch ? BALANCE.touch.headTrackingRate : undefined,
      // CPU by default, so MediaPipe does not open a second WebGL context next
      // to the renderer's (which makes the browser block the page); `?headtrackgpu=1`
      // opts into the GPU delegate on machines that want it.
      delegate: flags.has('headtrackgpu') ? 'GPU' : 'CPU',
      poseFilter: leanLegacy ? 'oneEuro' : leanNoSmoothing ? 'none' : 'kalman',
    });
    if (leanLegacy || flags.has('noinvert')) this.settings.set('invertHeadTracking', false);
    if (leanLegacy || flags.has('uncalibrated')) this.settings.set('displayCalibrated', false);

    // The stored screen calibration decides the head-offset scale and the
    // window distance; without one the shipped constants are used unchanged.
    this.applyScreenCalibration();

    if (!flags.has('norobot')) this.scene.add(this.robotView.group);
    if (!flags.has('noeffects')) {
      this.effects = new Effects(this.scene);
      this.grappleView = new GrappleView(this.scene);
    }

    if (!flags.has('nochamber')) {
      this.levelMeshes = buildLevelMeshes(this.scene, definition, built.room, this.modelLibrary);
    }
    if (!flags.has('nolights')) this.levelLights = buildLighting(this.scene, definition);
    this.renderEnabled = !flags.has('norender');

    // Prop models are a visual upgrade on the procedural cover, so only a real
    // renderer starts the load; the level is playable before they arrive.
    if (!options.createRenderer && !flags.has('nochamber')) {
      this.modelLibrary.load();
      void this.modelLibrary.whenReady().then(() => {
        if (this.disposed) return;
        if (this.levelMeshes) rebuildCover(this.levelMeshes, definition, this.modelLibrary);
        if (this.buildMode) this.refreshBuildAssets();
      });
    }

    this.hud = new Hud(uiRoot);
    this.menu = new Menu(uiRoot, {
      onStart: () => this.startRun(),
      onResume: () => this.resume(),
      onRestart: () => this.startRun(),
      onQuitToMenu: () => this.quitToMenu(),
      onToggleCamera: () => this.toggleCamera(),
      onToggleHeadTracking: () => void this.toggleHeadTracking(),
      onSettingsChanged: (patch) => this.applySettings(patch),
      onCalibrateHead: () => this.headTracker.recalibrate(),
      onCameraTestStart: () => void this.enterCameraTest(),
      onCameraTestStop: () => this.leaveCameraTest(),
      onSelectLevel: (id) => this.selectLevel(id),
      onOpenBuilder: () => this.openMapBuilder(),
      getLevels: () =>
        LEVEL_CATALOG.map((level) => ({
          id: level.id,
          name: level.name,
          difficulty: level.difficulty ?? 'standard',
        })),
      getSettings: () => this.settingsView(),
      getHeadTrackingStatus: () => this.headTrackingStatus(),
    });
    this.mapEditor = new MapEditor(uiRoot, this.mapStore, {
      onTest: (map) => this.testMap(map),
      onExit: () => this.closeMapBuilder(),
      onEnterBuild: (map) => this.enterBuildFromEditor(map),
    });
    this.calibrationStage = new CalibrationStage(uiRoot, {
      onStart: () => void this.startCalibrationCamera(),
      onStop: () => this.leaveCameraTest(),
      onRecalibrate: () => this.headTracker.recalibrate(),
      onOpenWizard: () => this.screenWizard.open(),
    });
    this.screenWizard = new ScreenCalibrationWizard(uiRoot, {
      getInitial: () => this.screenCalibration(),
      getSeparation: () => this.headTracker.lastSeparation,
      isTracking: () => this.headTracker.isActive,
      onSave: (data) => this.saveScreenCalibration(data),
      onClose: () => undefined,
    });
    this.scene.add(this.buildHologram.group);
    this.scene.add(this.droneRouteView.group);
    this.debugOverlay = new DebugOverlay(uiRoot);
    if (flags.has('debug')) {
      Debug.showOverlay = true;
      this.debugOverlay.setVisible(true);
    }
    this.touchControls = new TouchControls(uiRoot, this.input, canvas, {
      // Mobile browsers only start audio from a gesture, and the first touch
      // on the pad is the earliest one the game can rely on.
      onInteract: () => {
        if (this.audioEnabled) void this.audio.resume();
      },
      onTouchSeen: () => this.promoteTouch(),
    });
    this.touchControls.setLookSensitivity(this.settings.get('touchLookSensitivity'));

    this.input.attach(window, document);
    this.input.onPointerLockChange = (locked) => {
      if (locked) {
        this.hud.setPointerPrompt(false);
      } else if (this.phase === 'playing' && !this.touchActive()) {
        this.hud.setPointerPrompt(true);
      }
    };
    this.wireEvents();

    // A backgrounded tab pauses immediately. The resume button is then a user
    // gesture, so it can recapture the mouse reliably.
    this.onVisibilityChange = (): void => {
      if (document.hidden && this.phase === 'playing') this.pause();
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // A click while the pointer is free recaptures it; Input suppresses the
    // mouse action for that same click, so it cannot fire a shot.
    this.onCanvasMouseDown = (event: MouseEvent): void => {
      if (this.touchActive()) return;
      if (this.phase !== 'playing' || this.input.isPointerLocked) return;
      event.preventDefault();
      void this.capturePointer();
    };
    canvas.addEventListener('mousedown', this.onCanvasMouseDown);

    this.onPointerLockError = (): void => {
      if (this.phase === 'playing' && !this.touchActive()) this.hud.setPointerPrompt(true);
    };
    document.addEventListener('pointerlockerror', this.onPointerLockError);

    this.audio.setVolumes(
      this.settings.get('masterVolume'),
      this.settings.get('sfxVolume'),
      this.settings.get('ambienceVolume'),
      this.settings.get('musicVolume'),
      this.settings.get('muted'),
    );
    this.applyUiScale();
    this.hud.setReducedMotion(this.settings.get('reducedMotion'));
    this.robot.transitionSpeed = this.settings.get('transitionSpeed');
    this.robot.sidewaysTransitions = this.settings.get('sidewaysTransitions');
    this.cameraRig.setThirdPersonParallax(this.settings.get('thirdPersonParallax'));
    this.cameraRig.setInvertHeadTracking(this.settings.get('invertHeadTracking'));
    this.cameraRig.setPlaneSwivel(this.settings.get('cameraSwivel'));

    this.placePlayer();
    this.drones.spawn(this.built.targets, this.built.definition.seed);
    this.refreshDroneViews();

    this.loop = new GameLoop({
      update: (dt) => this.update(dt),
      render: (frameDelta) => this.render(frameDelta),
    });

    window.addEventListener('resize', () => this.resize());
    this.resize();
    this.applyTouchRuntime();

    // A lost WebGL context leaves a black screen with no way back, so it is
    // reported and the player is offered a reload
    // (see `docs/ARCHITECTURE.md` §13).
    this.onContextLost = (event: Event): void => {
      event.preventDefault();
      this.contextLost = true;
      showContextLostError();
    };
    this.onContextRestored = (): void => {
      this.contextLost = false;
      hideBootScreen();
    };
    canvas.addEventListener('webglcontextlost', this.onContextLost);
    canvas.addEventListener('webglcontextrestored', this.onContextRestored);
  }

  /** Release the context listeners and the level's GPU resources. */
  dispose(): void {
    this.disposed = true;
    // Free the WebGL context so a page that builds several games (tests, or a
    // reload that reuses the tab) does not run into the browser's context cap.
    const renderer = this.renderer as WebGLRenderer & {
      dispose?: () => void;
      forceContextLoss?: () => void;
    };
    renderer.forceContextLoss?.();
    renderer.dispose?.();
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
    this.canvas.removeEventListener('mousedown', this.onCanvasMouseDown);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('pointerlockerror', this.onPointerLockError);
    document.body.classList.remove('touch-mode');
    this.touchControls.dispose();
    this.input.dispose();
    this.clearBuildAssets();
    this.buildHologram.dispose();
    this.droneRouteView.dispose();
    this.bulletTimeOverlay.dispose();
    this.calibrationStage.setVisible(false);
    this.boltView.dispose();
    this.grappleView?.dispose();
    if (this.levelMeshes) {
      this.scene.remove(this.levelMeshes);
      disposeLevelMeshes(this.levelMeshes);
      this.levelMeshes = null;
    }
    if (this.levelLights) {
      this.scene.remove(this.levelLights);
      this.levelLights = null;
    }
  }

  /** The phase the game is in. */
  get phase(): GamePhase {
    return this.session.phase;
  }

  start(): void {
    this.loop.start();
    // A map that was handed over starts straight into it: a test run plays it,
    // a `?build=1` boot opens the room builder on it. The reload is the level
    // load, exactly as the level select works. If the map did not survive the
    // hand-off, the menu is shown instead.
    if (this.buildMode) {
      this.enterBuildMode();
      return;
    }
    if (this.customRun) {
      this.startRun();
      return;
    }
    const best = this.highScores.best;
    this.menu.showMain(best ? { score: best.score, time: best.time } : null);
    // The calibration stage is the opening screen, faded behind the menu, so a
    // head pose can be checked before the first run.
    this.menu.setGhost(true);
    this.calibrationStage.setVisible(true);
  }

  // ------------------------------------------------------------------ builder

  /** Open the 2D map builder over the menu. */
  private openMapBuilder(): void {
    this.hud.setVisible(false);
    this.menu.setVisible(false);
    // Resume the map that was last handed to the game, if there is one.
    this.mapEditor.open(this.mapStore.staged());
  }

  private closeMapBuilder(): void {
    this.mapEditor.setVisible(false);
    const best = this.highScores.best;
    this.menu.showMain(best ? { score: best.score, time: best.time } : null);
    // The calibration stage is the opening screen, faded behind the menu, so a
    // head pose can be checked before the first run.
    this.menu.setGhost(true);
    this.calibrationStage.setVisible(true);
  }

  /** Hand a map to the 3D room builder: the same reload, with `?build=1`. */
  private enterBuildFromEditor(map: MapDocument): void {
    this.mapStore.save(map);
    this.mapStore.stage(map);
    const url = new URL(window.location.href);
    url.searchParams.set('level', 'custom');
    url.searchParams.set('build', '1');
    window.location.assign(url.toString());
  }

  /**
   * Enter the room builder: a run on the staged map with the scanner up.
   *
   * Building reuses the ordinary run rather than adding a phase, so the walking,
   * the camera and the traversal are the ones the player already knows. What
   * makes it a *build* session is what is switched off: no shooting, no
   * grappling, no drones moving, and no hull to breach.
   */
  private enterBuildMode(): void {
    this.buildMode = true;
    this.startRun();
    this.buildHologram.update(null);
    this.refreshBuildAssets();
    this.hud.setBuild(true, this.buildController.describe());
    if (this.touchActive()) this.touchControls.setBuildModel(true);
  }

  /** Leave the builder and go back to the map, with the placements saved. */
  private exitBuildMode(): void {
    if (!this.buildMode) return;
    this.buildMode = false;
    this.buildHologram.setVisible(false);
    this.hud.setBuild(false);
    this.touchControls.setBuildModel(false);
    this.clearBuildAssets();
    this.droneRouteView.clear();
    this.mapStore.save(this.buildController.map);
    this.mapStore.stage(this.buildController.map);
    if (this.session.quit()) this.applyPhase('menu');
    this.openMapBuilder();
  }

  /** Redraw the meshes for the scanner's placements and the drone routes. */
  private refreshBuildAssets(): void {
    this.clearBuildAssets();
    const map = this.buildController.map;
    if (map.assets.length > 0) {
      this.buildAssets = buildObstacles(map.assets, this.modelLibrary);
      this.scene.add(this.buildAssets);
    }
    this.droneRouteView.setRoutes(
      map.drones.map((drone) => {
        const component = componentAt(map, drone.position[0], drone.position[2]);
        const key = component ? this.built.room.keyFor(component.id, drone.surface) : drone.surface;
        return {
          points: drone.route ?? [],
          surface: this.built.room.getSurface(key) ?? null,
          magnetic: drone.magnetic,
        };
      }),
    );
  }

  private clearBuildAssets(): void {
    if (!this.buildAssets) return;
    this.scene.remove(this.buildAssets);
    this.buildAssets.traverse((object) => {
      const mesh = object as { geometry?: { dispose(): void }; material?: { dispose(): void } };
      mesh.geometry?.dispose();
      mesh.material?.dispose();
    });
    this.buildAssets = null;
  }

  /**
   * One frame of the room builder: resolve the spot under the crosshair, run
   * the palette, and place or delete on the mouse.
   */
  private updateBuild(frame: InputFrame): void {
    const placement = this.buildController.preview(
      this.aimOrigin,
      this.aimDirection,
      BUILD_REACH,
      this.robot.position,
    );
    this.buildHologram.update(placement, this.buildController.isMagnetic);

    if (frame.buildNextPressed) this.buildController.cyclePiece();
    if (frame.buildVariantPressed) this.buildController.cycleVariant();
    if (frame.buildRotatePressed) this.buildController.rotate();
    if (frame.buildMagneticPressed) this.buildController.toggleMagnetic();
    if (frame.buildDronePressed) this.buildController.usePiece('drone');
    if (frame.buildWaypointPressed) this.buildController.usePiece('waypoint');
    if (frame.buildDeletePressed) {
      // Delete where the scanner is pointing — the snapped spot for a box, the
      // hit point for a drone — falling back to whatever the ray actually hit.
      if (placement) scratchPosition.set(...placement.position);
      else scratchPosition.copy(this.aimPoint);
      if (this.buildController.removeAt(scratchPosition)) this.onBuildChanged(true);
    }
    if (frame.grapplePressed && placement && this.buildController.place(placement)) {
      this.onBuildChanged(placement.kind === 'drone');
    }

    this.hud.setBuild(true, this.buildController.describe());
  }

  /**
   * A placement changed the map: save it and redraw what it affects.
   *
   * The collision half of the change is the controller's own (`syncCollision`),
   * so all this has to do is the visual half: the meshes for the boxes, and a
   * respawn for the drones, which are built from the level's targets.
   */
  private onBuildChanged(dronesChanged: boolean): void {
    // Saved *and* staged: the draft is what the editor lists, and the staged
    // map is what the editor (and the next test run) opens, so a build session
    // has to keep both current or the placements would vanish on the way back.
    this.mapStore.save(this.buildController.map);
    this.mapStore.stage(this.buildController.map);
    this.refreshBuildAssets();
    if (dronesChanged) this.respawnBuildDrones();
  }

  /**
   * Rebuild the level's drones from the map as it now stands.
   *
   * A placement has to be visible immediately, and the drones a level spawns
   * are built from its targets, so the builder respawns them from the document
   * it is editing. They do not move while building, so this is cheap.
   */
  private respawnBuildDrones(): void {
    const map = this.buildController.map;
    const placements = map.drones.map((drone) => {
      const component = componentAt(map, drone.position[0], drone.position[2]);
      const key = component ? this.built.room.keyFor(component.id, drone.surface) : drone.surface;
      return {
        surface: this.built.room.getSurface(key) ? key : drone.surface,
        position: new Vector3(drone.position[0], drone.position[1], drone.position[2]),
        magnetic: drone.magnetic,
        route: drone.route,
      };
    });
    this.drones.spawn(placements, this.built.definition.seed);
    this.refreshDroneViews();
  }

  /**
   * Save a map and boot into a run on it.
   *
   * The level is built once per page, so testing a map is a reload with the map
   * parked in session storage — the same shape as selecting a level from the
   * menu. Nothing about the running game has to be torn down or rebuilt.
   */
  private testMap(map: MapDocument): void {
    this.mapStore.save(map);
    this.mapStore.stage(map);
    const url = new URL(window.location.href);
    url.searchParams.set('level', 'custom');
    window.location.assign(url.toString());
  }

  // ------------------------------------------------------------------ events

  private wireEvents(): void {
    this.bus.on('shot', ({ endPoint }) => {
      this.audio.play('shot', 0.5);
      if (this.cameraRig.mode === 'first') {
        // In first person the shot leaves the eye along the camera axis, so the
        // tracer starts on the reticle instead of the world muzzle.
        this.cameraRig.camera.getWorldPosition(scratchMuzzle);
        this.cameraRig.getHeadAimDirection(this.robot, scratchHeadDirection);
        scratchMuzzle.addScaledVector(scratchHeadDirection, 0.2);
      } else {
        // Third person: the tracer and the flash leave the barrel the player
        // can see, and the tracer stops where the bullet actually landed.
        this.robotView.muzzleWorldPosition(scratchMuzzle);
      }
      this.effects?.spawnTracer(scratchMuzzle.clone(), endPoint);
      if (!this.settings.get('reducedMotion')) {
        this.effects?.spawnImpact(scratchMuzzle, 0xffe2a8, 1.1, 0.06);
      }
    });

    this.bus.on('ricochet', ({ from, to }) => {
      // One tracer leg per skip, from the bounce point outward, with a spark at
      // the surface it came off. The muzzle tracer stops at the first hit, so
      // the two together read as a banked shot rather than a straight line.
      this.effects?.spawnTracer(from.clone(), to.clone());
      if (!this.settings.get('reducedMotion')) {
        this.effects?.spawnImpact(from, 0x9ff6ff, 1.6, 0.12);
      }
      this.audio.playAt('hit', from, 0.4);
    });

    this.bus.on('hit', ({ position }) => {
      this.audio.playAt('hit', position, 0.6);
      this.effects?.spawnImpact(position);
    });

    this.bus.on('playerDamaged', ({ health }) => {
      this.hud.setHealth(health, BALANCE.player.maxHealth);
      this.audio.play('alarm', 0.4);
    });

    this.bus.on('playerDied', () => {
      this.endRun('gameover');
    });

    this.bus.on('scoreChanged', ({ score }) => {
      this.hud.setScore(score);
    });
  }

  // ------------------------------------------------------------------- phases

  /** Apply the effects of a phase change: HUD, menu, input and pointer lock. */
  private applyPhase(phase: GamePhase): void {
    if (this.appliedPhase === phase) return;
    this.appliedPhase = phase;
    this.bus.emit('phaseChanged', { phase });

    const playing = phase === 'playing';
    this.hud.setVisible(playing);
    this.menu.setVisible(!playing);
    this.input.setEnabled(playing);

    if (playing) {
      // The touch interface replaces the pointer lock entirely: a phone has
      // no mouse to capture, and asking for one only produces the prompt.
      if (!this.touchActive()) void this.capturePointer();
      else this.hud.setPointerPrompt(false);
    } else {
      this.input.exitPointerLock();
      this.hud.setPointerPrompt(false);
    }
    this.refreshTouchControls();
  }

  /** Capture the mouse and show the prompt only if the browser refuses. */
  private async capturePointer(): Promise<void> {
    if (this.touchActive()) {
      this.hud.setPointerPrompt(false);
      return;
    }
    const locked = await this.input.requestPointerLock(this.canvas);
    if (this.phase === 'playing' && !this.touchActive()) {
      this.hud.setPointerPrompt(!locked);
    }
  }

  /** True while the touch interface should drive the game. */
  private touchActive(): boolean {
    if (this.touchOverride !== null) return this.touchOverride;
    const mode = this.settings.get('touchControls');
    if (mode === 'on') return true;
    if (mode === 'off') return false;
    return this.touchDetected;
  }

  /**
   * A real touch on a device the heuristic missed (a touch-screen laptop, a
   * browser without the pointer media queries) turns the interface on.
   */
  private promoteTouch(): void {
    if (this.touchDetected) return;
    this.touchDetected = true;
    if (this.settings.get('touchControls') === 'auto') this.applyTouchRuntime();
  }

  /**
   * Apply the touch-specific renderer, tracker and HUD state.
   *
   * Phones get a lower pixel ratio and a slower head-tracking rate; the HUD
   * moves clear of the thumbs through the `touch-mode` class.
   */
  private applyTouchRuntime(): void {
    const touch = this.touchActive();
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, touch ? BALANCE.touch.pixelRatio : 2),
    );
    this.headTracker.setMaxRate(
      touch ? BALANCE.touch.headTrackingRate : BALANCE.headTracking.maxRate,
    );
    document.body.classList.toggle('touch-mode', touch);
    if (touch) this.hud.setPointerPrompt(false);
    this.refreshTouchControls();
  }

  /** Show the pad only while a round is running. */
  private refreshTouchControls(): void {
    this.touchControls.setVisible(this.touchActive() && this.phase === 'playing');
  }

  private startRun(): void {
    if (!this.session.begin()) return;
    // A run started from the camera test screen ends the test, keeping the
    // camera it already has.
    this.leaveCameraTest(true);
    if (this.audioEnabled) void this.audio.resume();
    this.audio.stopAllDroneHums();
    this.audio.startAmbience();
    this.score.reset();
    this.elapsed = 0;
    this.stepDistance = 0;
    this.moving = false;
    this.destroyedDrones.clear();
    this.weapon.reset();
    this.grapple.reset();
    this.netGun.reset();
    this.melee.reset();
    this.grappleView?.hide();
    this.bulletTime.reset();
    this.weaponMode = 'ar';
    this.bulletTimeForcedFirst = false;
    this.bulletTimeWasActive = false;
    this.hud.setWeapon('ar');
    this.hud.setWeaponCooldown(0);
    this.hud.setBulletTime(false);
    this.hud.setBulletTimeProgress(0, BALANCE.bulletTime.duration);
    this.hud.setHullWarning(false);
    this.hud.setSneak(false);
    this.hud.setDetected(false);
    this.hud.setPlanePin(false, false);
    this.headTilt.reset();
    this.hud.setPointerPrompt(false);
    this.hud.setReducedMotion(this.settings.get('reducedMotion'));
    this.cameraRig.reset();
    this.cameraRig.setMode(this.settings.get('cameraMode'), true);
    this.robotView.setFirstPerson(this.cameraRig.mode === 'first');
    this.robot.transitionSpeed = this.settings.get('transitionSpeed');
    this.robot.sidewaysTransitions = this.settings.get('sidewaysTransitions');
    this.placePlayer();

    this.drones.spawn(this.built.targets, this.built.definition.seed);
    this.refreshDroneViews();

    this.hud.setHealth(BALANCE.player.maxHealth, BALANCE.player.maxHealth);
    this.hud.setScore(0);
    this.hud.setDronesRemaining(this.drones.remaining);
    this.hud.setCameraMode(this.cameraRig.mode);
    this.hud.setObjective(
      this.touchActive()
        ? `Destroy the ${this.drones.list.length} drones — stick to move, drag to look, buttons to fight`
        : `Destroy the ${this.drones.list.length} drones — V view, T weapon, left fire, right grapple, F claw`,
    );
    this.applyPhase('playing');
    if (this.headTracker.isSynthetic || this.settings.get('headTrackingEnabled')) {
      void this.enableHeadTracking();
    } else if (this.headTracker.isActive) {
      this.disableHeadTracking();
    }
  }

  private resume(): void {
    if (!this.session.resume()) return;
    this.applyPhase('playing');
  }

  private pause(): void {
    if (!this.session.pause()) return;
    this.applyPhase('paused');
    this.menu.showPause();
  }

  private quitToMenu(): void {
    if (!this.session.quit()) return;
    this.audio.stopAllDroneHums();
    this.audio.stopAmbience();
    // The round is over, so the camera goes back: the setting is untouched, so
    // the next run starts tracking again (and the camera test asks for it).
    this.stopHeadTracking();
    this.applyPhase('menu');
    const best = this.highScores.best;
    this.menu.showMain(best ? { score: best.score, time: best.time } : null);
    // The calibration stage is the opening screen, faded behind the menu, so a
    // head pose can be checked before the first run.
    this.menu.setGhost(true);
    this.calibrationStage.setVisible(true);
  }

  private endRun(result: 'gameover' | 'victory'): void {
    if (!this.session.finish(result)) return;
    this.audio.stopAllDroneHums();
    this.audio.stopAmbience();
    this.applyPhase(result);
    if (result === 'gameover') {
      this.menu.showGameOver(this.score.current, this.drones.remaining);
    } else {
      this.highScores.submit(this.score.current, this.elapsed);
      const index = LEVEL_CATALOG.findIndex((level) => level.id === this.built.definition.id);
      const next = index >= 0 ? (LEVEL_CATALOG[index + 1] ?? null) : null;
      this.menu.showVictory(
        this.score.current,
        this.elapsed,
        this.highScores.best?.score ?? null,
        next ? { id: next.id, name: next.name } : null,
      );
    }
  }

  /** Leave the current run and load another level. */
  private selectLevel(id: string): void {
    const url = new URL(window.location.href);
    url.searchParams.set('level', id);
    window.location.assign(url.toString());
  }

  private placePlayer(): void {
    const built = this.built;
    const surface = built.room.getSurface(built.spawn.surface);
    if (!surface) return;
    const local = surface.toLocal(built.spawn.position);
    this.robot.reset(
      surface,
      Math.max(surface.uMin, Math.min(surface.uMax, local.u)),
      Math.max(surface.vMin, Math.min(surface.vMax, local.v)),
      built.spawn.facing,
    );
  }

  private refreshDroneViews(): void {
    for (const view of this.droneViews.values()) {
      this.scene.remove(view.group);
      view.dispose();
    }
    this.droneViews.clear();
    if (new URLSearchParams(window.location.search).has('nodrones')) return;
    for (const drone of this.drones.list) {
      const view = new DroneView();
      this.scene.add(view.group);
      this.droneViews.set(drone.id, view);
    }
  }

  // -------------------------------------------------------------------- input

  private toggleCamera(): void {
    const mode = this.cameraRig.toggleMode();
    // The player has taken the camera back, so the bullet-time window must not
    // restore its own forced view when it closes: that would silently undo the
    // switch, and with a stretched window there is a long time for it to bite.
    this.bulletTimeForcedFirst = false;
    this.robotView.setFirstPerson(mode === 'first');
    this.hud.setCameraMode(mode);
    this.audio.play('ui', 0.4);
  }

  private async enableHeadTracking(): Promise<void> {
    if (this.headTracker.isActive) return;

    this.hud.setHeadTracking(false, 'STARTING');
    // Keep the checkbox as the user set it while the camera starts; a failure
    // unticks it below, and a success confirms it.
    this.menu.setHeadTrackingStatus(true, 'STARTING — waiting for the camera');

    if (!this.headTracker.isSynthetic) {
      const modelUrl = new URL('assets/mediapipe/face_landmarker.task', document.baseURI).href;
      if (!(await this.assets.probe('head-tracking-model', modelUrl))) {
        const detail =
          this.assets.list.find((report) => report.id === 'head-tracking-model')?.detail ??
          'missing asset';
        this.settings.set('headTrackingEnabled', false);
        this.hud.setHeadTracking(false, 'OFF');
        this.hud.notify(`Head tracking model unavailable (${detail}).`, 5000);
        this.menu.setHeadTrackingStatus(false, `UNAVAILABLE — ${detail}`);
        this.bus.emit('headTrackingStatus', { active: false, reason: detail });
        return;
      }
    }

    const started = await this.headTracker.enable();
    if (!started) {
      this.settings.set('headTrackingEnabled', false);
      this.hud.setHeadTracking(false, 'OFF');
      this.hud.notify(this.headTracker.reason || 'Head tracking is unavailable.', 4000);
      this.menu.setHeadTrackingStatus(false, this.headTrackingStatus().text);
      this.bus.emit('headTrackingStatus', {
        active: false,
        reason: this.headTracker.reason,
      });
      return;
    }

    if (this.headTracker.isSynthetic) {
      this.hud.setHeadTracking(true, 'DEMO');
      this.hud.notify('Synthetic head tracking (demo). Watch the head and the aim.');
    } else {
      this.settings.set('headTrackingEnabled', true);
      this.hud.setHeadTracking(true, 'ON');
      this.hud.notify('Head tracking active. Look around to aim.');
    }
    this.menu.setHeadTrackingStatus(true, this.headTrackingStatus().text);
  }

  private disableHeadTracking(): void {
    if (!this.headTracker.isActive) return;
    this.headTracker.disable();
    this.cameraRig.clearHeadTracking();
    this.settings.set('headTrackingEnabled', false);
    this.hud.setHeadTracking(false, 'OFF');
    this.hud.notify('Head tracking disabled.');
    this.menu.setHeadTrackingStatus(false, this.headTrackingStatus().text);
  }

  private async toggleHeadTracking(): Promise<void> {
    if (this.headTracker.isActive) this.disableHeadTracking();
    else await this.enableHeadTracking();
  }

  /** A short, user-facing description of the head-tracking layer for the menu. */
  /** The stored screen calibration, sanitized. */
  private screenCalibration(): ScreenCalibrationData {
    return sanitizeScreenCalibration({
      screenWidthCm: this.settings.get('screenWidthCm'),
      screenHeightCm: this.settings.get('screenHeightCm'),
      viewingDistanceCm: this.settings.get('viewingDistanceCm'),
      calibrated: this.settings.get('displayCalibrated'),
    });
  }

  /**
   * Push the screen calibration into the head tracker and the camera.
   *
   * Uncalibrated, the fixed balance constants stand and nothing changes. Once
   * the wizard has been completed, the head offset is mapped to real metres
   * from the screen size and the virtual screen sits at the player's own
   * distance — so the parallax speed and depth match their actual setup.
   */
  private applyScreenCalibration(): void {
    const calibration = this.screenCalibration();
    const scale = calibration.calibrated
      ? calibratedViewScale(calibration)
      : { x: BALANCE.headTracking.viewScaleX, y: BALANCE.headTracking.viewScaleY };
    this.cameraRig.setInvertHeadTracking(this.settings.get('invertHeadTracking'));
    this.cameraRig.setWindowDistance(
      calibration.calibrated
        ? viewingDistanceMetres(calibration)
        : BALANCE.camera.offAxis.screenDistance,
    );
    // The reach the offset is clamped and normalised against: real metres once
    // calibrated, the shipped absolutes otherwise, so the feel is the same
    // fraction of a lean either way.
    this.viewerReach = viewerReachFor(calibration);
    this.cameraRig.setViewerReach(this.viewerReach.x, this.viewerReach.y, this.viewerReach.forward);
    // The neutral pose is stored in scaled units, so a live scale change needs
    // a fresh one or the window would jump. A tracker that is not running has
    // its neutral reset when it starts, so there is nothing to re-capture.
    const changed = this.headTracker.setViewScale(scale.x, scale.y);
    if (changed && this.headTracker.isActive) this.headTracker.recalibrate();
  }

  private saveScreenCalibration(data: ScreenCalibrationData): void {
    this.settings.set('screenWidthCm', data.screenWidthCm);
    this.settings.set('screenHeightCm', data.screenHeightCm);
    this.settings.set('viewingDistanceCm', data.viewingDistanceCm);
    this.settings.set('displayCalibrated', data.calibrated);
    this.applyScreenCalibration();
    this.hud.notify(
      data.calibrated
        ? `Screen calibrated: ${Math.round(data.screenWidthCm)} cm at ${Math.round(data.viewingDistanceCm)} cm`
        : 'Screen calibration cleared',
      3500,
    );
  }

  private headTrackingStatus(): { enabled: boolean; text: string } {
    if (this.headTracker.isSynthetic) {
      return this.headTracker.isActive
        ? { enabled: true, text: 'DEMO — synthetic head motion' }
        : { enabled: false, text: 'DEMO — press H during a run to start' };
    }
    if (this.headTracker.isActive) {
      const cost = this.headTracker.lastInferenceDurationMs;
      return {
        enabled: true,
        text: cost > 0 ? `ON — tracking · ${cost.toFixed(1)} ms` : 'ON — tracking',
      };
    }
    switch (this.headTracker.status) {
      case 'starting':
        // Still a live request: the checkbox stays ticked and the camera test
        // says what it is waiting for rather than claiming the feature is off.
        return { enabled: true, text: 'STARTING — waiting for the camera' };
      case 'denied':
        return { enabled: false, text: `DENIED — ${this.headTracker.reason}` };
      case 'unavailable':
        return { enabled: false, text: `UNAVAILABLE — ${this.headTracker.reason}` };
      case 'error':
        return { enabled: false, text: `ERROR — ${this.headTracker.reason}` };
      default:
        // The camera is released outside a round, so "idle" is not the same as
        // unwanted: say which of the two this is rather than claiming the
        // feature is off when the player has it on.
        return this.settings.get('headTrackingEnabled')
          ? { enabled: true, text: 'READY — starts with a run or the camera test' }
          : { enabled: false, text: 'OFF — needs a webcam and camera permission' };
    }
  }

  private settingsView(): SettingsView {
    return {
      lookSensitivity: this.settings.get('lookSensitivity'),
      touchLookSensitivity: this.settings.get('touchLookSensitivity'),
      invertY: this.settings.get('invertY'),
      invertLookX: this.settings.get('invertLookX'),
      invertHeadTracking: this.settings.get('invertHeadTracking'),
      fov: this.settings.get('fov'),
      cameraMode: this.settings.get('cameraMode'),
      transitionSpeed: this.settings.get('transitionSpeed'),
      sidewaysTransitions: this.settings.get('sidewaysTransitions'),
      horizonLock: this.settings.get('horizonLock'),
      reducedMotion: this.settings.get('reducedMotion'),
      headTrackingEnabled:
        this.settings.get('headTrackingEnabled') ||
        (this.headTracker.isSynthetic && this.headTracker.isActive),
      headTrackingSensitivity: this.settings.get('headTrackingSensitivity'),
      headTrackingBodyShift: this.settings.get('headTrackingBodyShift'),
      dodgeSwivelRange: this.settings.get('dodgeSwivelRange'),
      thirdPersonParallax: this.settings.get('thirdPersonParallax'),
      cameraSwivel: this.settings.get('cameraSwivel'),
      masterVolume: this.settings.get('masterVolume'),
      sfxVolume: this.settings.get('sfxVolume'),
      ambienceVolume: this.settings.get('ambienceVolume'),
      musicVolume: this.settings.get('musicVolume'),
      muted: this.settings.get('muted'),
      uiScale: this.settings.get('uiScale'),
      touchControls: this.settings.get('touchControls'),
    };
  }

  private applyUiScale(): void {
    this.uiRoot.style.setProperty('--ui-scale', String(this.settings.get('uiScale')));
  }

  private applySettings(patch: Partial<SettingsView>): void {
    for (const [key, value] of Object.entries(patch) as [
      keyof SettingsView,
      SettingsView[keyof SettingsView],
    ][]) {
      this.settings.set(key as never, value as never);
    }
    this.cameraRig.setFov(this.settings.get('fov'));
    this.audio.setVolumes(
      this.settings.get('masterVolume'),
      this.settings.get('sfxVolume'),
      this.settings.get('ambienceVolume'),
      this.settings.get('musicVolume'),
      this.settings.get('muted'),
    );
    this.applyUiScale();
    this.hud.setReducedMotion(this.settings.get('reducedMotion'));
    this.robot.transitionSpeed = this.settings.get('transitionSpeed');
    this.robot.sidewaysTransitions = this.settings.get('sidewaysTransitions');
    this.cameraRig.setThirdPersonParallax(this.settings.get('thirdPersonParallax'));
    this.cameraRig.setInvertHeadTracking(this.settings.get('invertHeadTracking'));

    if (patch.cameraMode !== undefined) {
      this.cameraRig.setMode(patch.cameraMode);
      this.robotView.setFirstPerson(this.cameraRig.mode === 'first');
      this.hud.setCameraMode(this.cameraRig.mode);
    }
    if (patch.headTrackingEnabled !== undefined) {
      if (patch.headTrackingEnabled) void this.enableHeadTracking();
      else this.disableHeadTracking();
    }
    if (patch.touchControls !== undefined) this.applyTouchRuntime();
    if (patch.touchLookSensitivity !== undefined) {
      this.touchControls.setLookSensitivity(this.settings.get('touchLookSensitivity'));
    }
    if (patch.cameraSwivel !== undefined) {
      this.cameraRig.setPlaneSwivel(this.settings.get('cameraSwivel'));
    }
  }

  // ------------------------------------------------------------------- update

  /**
   * Watch for the head-tilt gesture that settles the camera onto a new wall.
   *
   * A big, deliberate roll past the threshold, held for a moment, is the
   * player saying "I am ready to see this wall as the floor"; the rig then runs
   * the slow swivel. `Q` pulls the same trigger with no camera at all. The
   * indicator under the reticle tells the player the view is waiting.
   */
  private updatePlaneSwivel(dt: number): void {
    const pinned = this.cameraRig.isPlanePinned;
    this.hud.setPlanePin(pinned, this.headTracker.isActive);
    if (!pinned) {
      this.headTilt.reset();
      return;
    }
    const roll = this.headTracker.isActive ? this.headTracker.headRoll : 0;
    if (this.headTilt.update(roll, dt)) this.cameraRig.swivelToBodyPlane();
  }

  /**
   * Poll the head tracker and apply this frame's viewer offset.
   *
   * The tracker is a view-position layer: it moves the viewpoint and the
   * frustum and never touches the aim. `bodyShift` is the first-person lean
   * swivel, which is gameplay (a bounded offset that always returns home, plus
   * the bullet-time near-miss assist) rather than part of the window, so the
   * menu's camera test runs the same viewer offset without it.
   */
  private updateHeadTracking(dt: number, bodyShift: boolean): void {
    const pose = this.headTracker.update(performance.now(), dt);
    if (pose) {
      // A missed frame (rate gate, blink, hidden tab) should not snap the
      // window or the dodge back to neutral, so hold the last pose briefly.
      this.headTrackingLossTimer = 0.25;
      const intensity = this.settings.get('headTrackingSensitivity');
      const reduced = this.settings.get('reducedMotion')
        ? BALANCE.camera.offAxis.reducedMotionScale
        : 1;
      const amount = intensity * reduced * BALANCE.headTracking.leanFeelBoost;
      // `z` is the distance to the camera, so moving closer is a positive
      // forward offset. The lateral sign is flipped inside the rig, from the
      // invert setting, so the whole pipeline (window and dodge) follows. The
      // pose is already smoothed in the tracker (a constant-velocity Kalman on
      // the whole signal, at all times), so there is no second smoothing stage
      // here and no special case for the dodge window.
      this.cameraRig.applyViewOffset(pose.x * amount, pose.y * amount, -pose.z * amount);
      // What the rig actually applied (clamped, and inverted if the setting is
      // on), so the body dodge and the window move together.
      const applied = this.cameraRig.headViewOffset;
      // The lean lock: while the dodge window is open, re-aim onto the target
      // the lean would otherwise slide off, so the body swivels out while the
      // view stays locked on the attacker instead of panning.
      if (this.bulletTime.active) {
        // The eye has moved by the tracked offset *and* by the body sway. Both
        // are cancelled, so the dodge pivots about the aim point and the view
        // stays on the target instead of panning with the body.
        const camera = this.cameraRig.camera;
        scratchLeanRight.setFromMatrixColumn(camera.matrixWorld, 0);
        scratchLeanUp.setFromMatrixColumn(camera.matrixWorld, 1);
        this.robot.swayWorldOffset(scratchSwayWorld);
        const lateral = applied.x + scratchSwayWorld.dot(scratchLeanRight);
        const vertical = applied.y + scratchSwayWorld.dot(scratchLeanUp);
        const range = this.aimPoint.distanceTo(camera.position);
        const lock = leanLock(lateral, vertical, range);
        this.cameraRig.setLeanLock(lock.yaw, lock.pitch);
      } else {
        this.cameraRig.setLeanLock(0, 0);
      }
      // In first person the lean can also shove the body sideways, which reads
      // as a dodge rather than a camera-only shift. A centre dead zone keeps a
      // stable aim, and past it the request ramps up from zero.
      if (
        bodyShift &&
        this.cameraRig.mode === 'first' &&
        this.settings.get('headTrackingBodyShift')
      ) {
        const bullet = this.bulletTime.active;
        bodyShiftFromOffset(
          applied.x,
          -applied.z,
          robotCommand.headShift,
          {
            deadZone: bullet ? BALANCE.bulletTime.shiftDeadZone : this.viewerReach.deadZone,
            maxLateral: this.viewerReach.x,
            maxVertical: this.viewerReach.y,
            maxForward: this.viewerReach.forward,
          },
          bullet ? applied.y : 0,
        );
        // The window magnifies the swing through the range rather than the
        // request, so the body's displacement stays proportional to how far
        // the head has moved from centre.
        robotCommand.swayRange = bullet
          ? this.settings.get('dodgeSwivelRange')
          : BALANCE.headTracking.swayRange;
        // Near-miss assist: push the body away from the predicted bolt path,
        // including the vertical component, so ducking also clears a bolt.
        // `?noassist` drops it, so a feel problem can be bisected on a page.
        if (bullet && !this.assistDisabled) {
          this.bulletTime.getAssist(scratchAssist);
          if (scratchAssist.lengthSq() > 1e-8) {
            scratchAssist.normalize();
            robotCommand.headShift.x +=
              scratchAssist.dot(this.cameraRig.getViewRight(scratchPull)) * 0.6;
            robotCommand.headShift.z +=
              scratchAssist.dot(this.cameraRig.getViewForward(scratchPull)) * 0.6;
            robotCommand.headShift.y += scratchAssist.dot(this.robot.up) * 0.6;
          }
        }
      } else {
        robotCommand.headShift.x = 0;
        robotCommand.headShift.y = 0;
        robotCommand.headShift.z = 0;
      }
      return;
    }

    // No fresh pose. If tracking is off, or the face has been gone longer than
    // the grace window, return the window and the dodge to neutral.
    this.headTrackingLossTimer -= dt;
    if (!this.headTracker.isActive || this.headTrackingLossTimer <= 0) {
      this.cameraRig.clearHeadTracking();
      this.cameraRig.setLeanLock(0, 0);
      robotCommand.headShift.x = 0;
      robotCommand.headShift.y = 0;
      robotCommand.headShift.z = 0;
    }
  }

  /**
   * The menu's camera test.
   *
   * It runs the same viewpoint pipeline as a round — tracker, off-axis window,
   * third-person camera — with the world frozen, so the webcam setup can be
   * checked and the window seen to move before a run is started. The live
   * readout under the panel is the proof that poses are arriving.
   */
  /**
   * Draw every bolt through the focus frame, at its true transform.
   *
   * While the window is open the world bolt layer is hidden and every active
   * bolt is handed to the overlay as-is: the overlay pass draws after the frame,
   * so a bolt crossing the bar band rides over the bars, and when its head
   * leaves the viewport the frustum clips it while the trail — which extends
   * back along the path — is still on screen and sweeps past. There is a single
   * continuous bolt for the whole window, no swap and no resize. A bolt that has
   * travelled its own length behind the eye has gone by and is dropped.
   */
  private updateBoltPasses(): void {
    const visuals = this.boltPopVisuals;
    visuals.length = 0;
    const camera = this.cameraRig.camera;
    // Refreshes the camera's world matrix; the depth test reads the view axis.
    camera.getWorldDirection(scratchForward);
    const behindDistance = BALANCE.combat.boltPopBehindDistance;
    for (const bolt of this.drones.bolts.active) {
      const depth = scratchBoltOffset
        .subVectors(bolt.position, camera.position)
        .dot(scratchForward);
      if (depth < -behindDistance) continue;
      const visual = this.boltPopPool[visuals.length];
      if (!visual) break;
      visual.position.copy(bolt.position);
      visual.velocity.copy(bolt.velocity);
      visual.scale = 1;
      visuals.push(visual);
    }
    this.bulletTimeOverlay.sync(visuals);
  }

  /** Project a world point to screen pixels, false when it is behind the camera. */
  private projectToScreen(world: Vector3, out: { x: number; y: number }): boolean {
    scratchProjected.copy(world).project(this.cameraRig.camera);
    if (scratchProjected.z < -1 || scratchProjected.z > 1) return false;
    out.x = (scratchProjected.x * 0.5 + 0.5) * window.innerWidth;
    out.y = (-scratchProjected.y * 0.5 + 0.5) * window.innerHeight;
    return true;
  }

  /**
   * Ring the crosshair with a red triangle per drone currently firing.
   *
   * The bearing is measured from the aim direction, so it reads relative to
   * where the player is pointing, and one marker per attacker tells a pincer
   * from a single shooter.
   */
  private updateThreats(): void {
    const bearings = this.threatBearings;
    bearings.length = 0;
    this.cameraRig.getAimDirection(scratchForward);
    for (const drone of this.drones.list) {
      if (drone.isDestroyed || !drone.isEngaging) continue;
      bearings.push(bearingFrom(scratchForward, this.robot.up, drone.position));
    }
    this.hud.setThreatBearings(bearings);
  }

  private updateCameraTest(dt: number): void {
    this.updateHeadTracking(dt, false);
    this.cameraRig.update(dt, this.robot, this.settings.get('horizonLock'));
    const status = this.headTrackingStatus();
    const viewer = formatViewerOffset(this.cameraRig.headViewOffset);
    this.menu.setCameraTestLive(status, viewer);
    this.calibrationStage.setStatus(status.text, status.enabled);
    this.calibrationStage.setViewer(viewer);

    // Feed the map: the head in the camera frame, and where the filtered offset
    // maps on the screen. Both are normalised so the panel is resolution-free.
    const off = BALANCE.camera.offAxis;
    this.calibrationStage.sample(
      this.headTracker.lastFramePosition,
      this.cameraRig.headViewOffset.x / off.maxViewerX,
      this.cameraRig.headViewOffset.y / off.maxViewerY,
      performance.now() / 1000,
    );
  }

  /**
   * Show and feed the calibration stage.
   *
   * The stage is the opening screen: it lives in the menu phase, in front of
   * the faded menu backdrop, and follows the tracker's on/off state so the
   * standby "Start camera" button and the running preview swap cleanly.
   */
  private updateCalibrationStage(): void {
    const active = this.phase === 'menu' && this.menu.visible;
    if (this.calibrationStage.visible !== active) {
      this.calibrationStage.setVisible(active);
      this.menu.setGhost(active);
    }
    if (!active) return;

    const tracking = this.headTracker.isActive;
    if (this.calibrationStage.isTracking !== tracking) {
      this.calibrationStage.setTracking(tracking);
    }
    // The map's dead-zone box follows the calibrated reach.
    this.calibrationStage.map.setReach(this.viewerReach.deadZone, this.viewerReach.x);
    // Draw every frame so the guides and the trail's fade stay live even when
    // the camera is off.
    if (this.calibrationStage.isTracking) this.calibrationStage.render(performance.now() / 1000);
  }

  /**
   * Open the camera test.
   *
   * The click that opened it is the user gesture, so this is where the camera
   * is asked for: never at page load, which browsers throttle or block.
   */
  private async enterCameraTest(): Promise<void> {
    this.cameraTestActive = true;
    this.placePlayer();
    this.cameraRig.reset();
    if (this.settings.get('headTrackingEnabled') && !this.headTracker.isActive) {
      await this.enableHeadTracking();
    }
  }

  /**
   * Start tracking from the calibration stage's own button.
   *
   * Unlike the menu item, this ignores the stored preference: pressing Start
   * camera *is* the request, so a player who had tracking off can turn it on
   * for the calibration without a trip to Settings first.
   */
  private async startCalibrationCamera(): Promise<void> {
    this.cameraTestActive = true;
    this.placePlayer();
    this.cameraRig.reset();
    if (!this.headTracker.isActive) await this.enableHeadTracking();
  }

  /**
   * Leave the camera test.
   *
   * `forRun` hands the live camera to the round that is starting, so tracking
   * never stops and restarts between the test and the run. Everywhere else the
   * camera is released: the tracker belongs to a live round (or to the camera
   * test), and a webcam light left on while browsing a menu is rude.
   */
  private leaveCameraTest(forRun = false): void {
    if (!this.cameraTestActive) return;
    this.cameraTestActive = false;
    if (!forRun) this.stopHeadTracking();
  }

  /**
   * Release the camera but keep the preference.
   *
   * `disableHeadTracking` is the user-facing switch, which also clears the
   * setting so the next run does not start tracking again. This is the
   * lifecycle version: the player still wants the window, they are just not in
   * a round.
   */
  private stopHeadTracking(): void {
    if (!this.headTracker.isActive) return;
    this.headTracker.disable();
    this.cameraRig.clearHeadTracking();
    this.hud.setHeadTracking(false, 'OFF');
    this.menu.setHeadTrackingStatus(false, this.headTrackingStatus().text);
  }

  private update(dt: number): void {
    const frame = this.input.sample();
    this.lastInput = frame;

    if (frame.debugPressed) {
      Debug.showOverlay = !Debug.showOverlay;
      this.debugOverlay.setVisible(Debug.showOverlay);
    }

    if (frame.pausePressed && this.buildMode) {
      // Escape leaves the builder rather than pausing it: there is no fight to
      // pause, and the map editor is where the player wants to be.
      this.exitBuildMode();
    } else if (frame.pausePressed) {
      if (this.phase === 'playing') this.pause();
      else if (this.phase === 'paused') this.resume();
    }

    // Restarting a build session would throw away nothing (every placement is
    // saved as it lands) but it is not what the player is asking for, and `R`
    // is the scanner's rotate key while building.
    if (frame.restartPressed && this.phase !== 'menu' && !this.buildMode) {
      this.startRun();
    }

    // The camera test lives in the menu, where both of these are also useful:
    // preview the other viewpoint, or start the camera after a refusal.
    const cameraControlsLive = this.phase === 'playing' || this.cameraTestActive;

    if (frame.toggleCameraPressed && cameraControlsLive) {
      this.toggleCamera();
    }

    if (frame.toggleHeadTrackingPressed && cameraControlsLive) {
      void this.toggleHeadTracking();
    }

    if (frame.switchWeaponPressed && this.phase === 'playing' && !this.buildMode) {
      this.weaponMode = this.weaponMode === 'ar' ? 'net' : 'ar';
      this.hud.setWeapon(this.weaponMode);
      this.audio.play('ui', 0.4);
    }

    this.updateCalibrationStage();

    if (this.phase !== 'playing') {
      // Outside a round only the menu's camera test runs. It keeps the
      // viewpoint and the off-axis window live behind the panel so the webcam
      // setup can be checked; the world itself is frozen.
      if (this.cameraTestActive) this.updateCameraTest(dt);
      this.hud.setThreatBearings(NO_THREATS);
      this.bulletTimeOverlay.setEnabled(false);
      return;
    }

    this.elapsed += dt;

    if (!this.buildMode) {
      // Settle the view onto the plane the body just stepped onto: `Q`, or a
      // deliberate head tilt while a webcam is running.
      if (frame.swivelViewPressed) this.cameraRig.swivelToBodyPlane();
      this.updatePlaneSwivel(dt);
    }

    // Bullet time is evaluated on real time before anything slows down. There
    // is nothing to dodge while building, so the clock stays at one.
    if (!this.buildMode) {
      this.bulletTime.update(dt, this.drones.bolts.active, this.robot.position);
    }
    const timeScale = this.buildMode ? 1 : this.bulletTime.timeScale;
    if (this.bulletTime.consumeTrigger()) this.triggerBulletTime();
    const bulletActive = !this.buildMode && this.bulletTime.active;
    if (bulletActive !== this.bulletTimeWasActive) {
      this.bulletTimeWasActive = bulletActive;
      this.hud.setBulletTime(bulletActive);
      if (!bulletActive && this.bulletTimeForcedFirst) {
        this.bulletTimeForcedFirst = false;
        this.cameraRig.setMode('third');
        this.robotView.setFirstPerson(false);
        this.hud.setCameraMode('third');
      }
    }
    if (bulletActive) {
      // The window is stretched to cover the bolts still in the air, so the bar
      // is scaled by the window's own length rather than the base duration.
      this.hud.setBulletTimeProgress(this.bulletTime.remaining, this.bulletTime.span);
      // The engine draws the frame so real bolts can fly over it. Reduced
      // motion keeps it too — the frame and the pop-out are game information,
      // not a flash — and only the impact flash and the head-tracked strength
      // are suppressed. The CSS bars remain just for a no-render boot.
      const canvasFrame = this.renderEnabled;
      this.hud.setFrameMode(canvasFrame ? 'canvas' : 'dom');
      this.bulletTimeOverlay.setEnabled(canvasFrame);
      this.updateBoltPasses();
    } else {
      this.bulletTimeOverlay.setEnabled(false);
    }

    // The mouse stays on the real clock during bullet time: the world slows to
    // make the dodge readable, but the player's hand does not. The body still
    // runs on the slowed clock (that is the dodge); only the aim keeps its
    // normal sensitivity, matching the head-tracking lean.
    this.cameraRig.applyLook(
      frame.look.x * (this.settings.get('invertLookX') ? -1 : 1),
      frame.look.y,
      this.settings.get('lookSensitivity'),
      this.settings.get('invertY'),
    );

    this.updateHeadTracking(dt, true);

    this.cameraRig.updateViewBasis(this.robot);

    const previousState = this.robot.state;
    // The body runs on the slowed clock during bullet time; the head tracker
    // stays at full rate, which is what makes the lean the dodge. The move
    // vector is passed through untouched because the scale now lives in
    // `Robot`, where it also covers turning, jumping and blends.
    robotCommand.move.x = frame.move.x;
    robotCommand.move.y = frame.move.y;
    robotCommand.timeScale = timeScale;
    robotCommand.jumpPressed = frame.jumpPressed;
    robotCommand.crouchHeld = frame.crouchHeld;
    robotCommand.sneakHeld = frame.sneakHeld;
    robotCommand.grabHeld = frame.grabHeld;
    // The steering basis, not the raw view basis: while the picture is held on
    // the plane the player just left, it is carried onto the new surface, so W
    // keeps meaning "the way I was going" (up the wall you walked into) instead
    // of collapsing onto the seam. See `CameraRig.getSteerBasis`.
    this.cameraRig.getSteerBasis(this.robot, robotCommand.viewForward, robotCommand.viewRight);
    // The jump leaves along the reticle target. The aim is one frame old here
    // (it is resolved after the body moves), which is imperceptible for a
    // launch and keeps the robot update independent of the camera update.
    scratchJump.copy(this.aimPoint).sub(this.robot.position);
    if (scratchJump.lengthSq() < 1e-8) scratchJump.copy(this.aimDirection);
    if (scratchJump.lengthSq() < 1e-8) scratchJump.copy(this.robot.up);
    robotCommand.jumpDirection.copy(scratchJump).normalize();
    this.robot.update(dt, robotCommand);
    this.updatePlayerAudio(previousState, dt);
    this.hud.setSneak(this.robot.sneakAmount > 0.2);

    if (!this.buildMode) {
      this.grapple.update(dt * timeScale);
      this.weapon.update(dt * timeScale);
      this.netGun.update(dt * timeScale);
      this.melee.update(dt * timeScale);
      this.hud.setWeaponCooldown(this.netGun.cooldownRatio);
      this.cameraRig.setRecoil(this.weapon.recoilAmount);
    }
    this.cameraRig.update(dt, this.robot, this.settings.get('horizonLock'));

    // The aim is resolved every frame: the reticle marks the point the gun is
    // pointing at, and the scanner marks where the piece would land.
    this.computeAim();

    if (this.buildMode) {
      this.updateBuild(frame);
      this.hud.setThreatBearings(NO_THREATS);
    } else {
      this.updateHullWarning();

      if (frame.firePressed) {
        this.fireCurrentWeapon();
      }

      if (frame.grapplePressed) {
        this.fireGrapple();
      }

      if (frame.meleePressed) {
        this.tryMelee();
      }
      this.grapple.step(dt * timeScale, this.drones.list);
      this.applyGrapplePull(dt * timeScale);

      const worldDt = dt * timeScale;
      this.drones.update(worldDt, this.robot.position, this.robot.velocity);
      this.hud.setDetected(this.drones.anyAlerted);
      this.flashNewBolts();

      this.drones.bolts.update(
        worldDt,
        this.collision,
        this.robot.position,
        BALANCE.player.bodyRadius,
        (damage, hitPosition, source) => {
          this.robot.takeDamage(damage);
          // A bolt knocks the body back along its travel: a shove on a surface,
          // and in free flight it carries (a hit floats you off).
          scratchBoltDirection.subVectors(hitPosition, source);
          this.robot.applyImpulse(scratchBoltDirection, BALANCE.combat.boltKnockback);
          this.bus.emit('playerDamaged', { amount: damage, health: this.robot.hull });
          this.effects?.spawnImpact(hitPosition, 0xff3a2a, 2.6, 0.18);
          this.audio.playAt('hit', hitPosition, 0.5);
          const severity = Math.min(1, Math.max(0.35, damage / 10));
          // A screen flash wherever the bolt lands, so a hit is unmistakable — a
          // bolt coming straight at the camera flashes from the centre out. If
          // the point will not project, flash the middle rather than nothing.
          const projected = this.projectToScreen(hitPosition, scratchBoltHead);
          const hitX = projected ? scratchBoltHead.x : window.innerWidth / 2;
          const hitY = projected ? scratchBoltHead.y : window.innerHeight / 2;
          this.hud.showBoltHit(hitX, hitY, severity);
          // During the dodge window the hit also glows in the focus frame.
          if (this.bulletTime.active && !this.settings.get('reducedMotion')) {
            this.hud.showBoltImpact(hitX, hitY);
          }
          this.cameraRig.getAimDirection(scratchForward);
          this.hud.showDamageDirection(
            bearingFrom(scratchForward, this.robot.up, source),
            1100,
            severity,
          );
        },
        (hitPosition) => {
          this.effects?.spawnImpact(hitPosition, 0xffb27a, 1.2, 0.1);
        },
      );

      this.updateDroneState();
      this.updateThreats();
    }

    this.hud.setHealth(this.robot.hull, BALANCE.player.maxHealth);
    this.hud.setDronesRemaining(this.drones.remaining);
    this.hud.setSurface(this.robot.surface?.id ?? null);
    this.updateRadar();

    // A build session cannot be lost, so neither death nor a cleared deck ends
    // it: the player leaves with Escape.
    if (this.buildMode) return;

    // Death is noticed here rather than at the point of damage, so any source
    // of damage that kills the player ends the round.
    if (!this.robot.isAlive && this.phase === 'playing') {
      this.bus.emit('playerDied', {});
    }

    if (this.drones.allDestroyed) {
      this.bus.emit('levelComplete', { score: this.score.current });
      this.endRun('victory');
    }
  }

  /** Player-owned sounds and landing feedback driven by locomotion changes. */
  /**
   * Work out where the next shot comes from and where it is going.
   *
   * In first person the camera is the aim device: the shot comes from the eye
   * along the camera's direction, which is the mouse axis and nothing else. The
   * world-anchored reticle marks it, sitting on the screen centre when there is
   * no viewer offset and at the window's principal point when there is.
   *
   * In third person the player aims with a world-anchored reticle. The head
   * ray decides *where on screen* the reticle sits, but the reticle is a
   * screen-space marker: the camera sits 4.5 m behind the head, so the ray
   * from the head and the ray from the camera towards the same screen pixel
   * diverge by metres at a few metres out. Resolving the shot along the head
   * ray would miss a drone the player can see under the crosshair. The target
   * is therefore resolved through the camera, and the camera ray is also the
   * shot ray so a doorway cannot stop a shot the player can see through.
   */
  private computeAim(): void {
    if (this.cameraRig.mode === 'first') {
      // The eye is the aim device, so the shot leaves it along the camera
      // axis. That axis belongs to the mouse alone: the head tracker only
      // translates the viewpoint, so leaning cannot swing the shot off the
      // world point the player is pointing at.
      this.cameraRig.camera.getWorldPosition(this.aimOrigin);
      // A lean can push the eye through a thin wall; the shot is fired from the
      // body's own anchor in that case, or it would pass through the wall.
      scratchAimAnchor
        .copy(this.robot.position)
        .addScaledVector(this.robot.up, BALANCE.player.attachHeight);
      clampShotOrigin(this.collision, this.aimOrigin, scratchAimAnchor, this.aimOrigin);
      this.cameraRig.getAimDirection(this.aimDirection);
      this.resolveAimPoint(this.aimOrigin, this.aimDirection);
      return;
    }

    this.cameraRig.getHeadPosition(this.robot, scratchHeadPosition);
    this.cameraRig.getHeadAimDirection(this.robot, scratchHeadDirection);
    this.cameraRig.camera.getWorldPosition(scratchCameraPosition);
    this.cameraRig.getAimDirection(scratchCameraForward);

    resolveThirdPersonAim(
      scratchCameraPosition,
      scratchCameraForward,
      scratchHeadPosition,
      scratchHeadDirection,
      this.collision,
      this.drones.weaponTargets,
      this.aimSolution,
    );
  }

  /** The nearest world point along a ray, clamped to the weapon's range. */
  private resolveAimPoint(origin: Vector3, direction: Vector3): void {
    const hit = this.collision.raycast(origin, direction, BALANCE.combat.range);
    if (hit) {
      this.aimPoint.copy(hit.point);
    } else {
      this.aimPoint.copy(origin).addScaledVector(direction, BALANCE.combat.range);
    }
  }

  /** Fire the equipped weapon: the AR or the capture net. */
  private fireCurrentWeapon(): boolean {
    this.computeAim();
    this.drones.hearNoise(this.robot.position, BALANCE.stealth.shotNoiseRadius);
    if (this.weaponMode === 'net') {
      const caught = this.netGun.tryFire(this.aimOrigin, this.aimDirection, this.drones.list);
      // A visible net burst makes a miss read as a shot instead of a dead
      // input, and a caught drone gets a tracer straight to the target.
      const end = caught
        ? caught.position
        : scratchProjected
            .copy(this.aimOrigin)
            .addScaledVector(this.aimDirection, BALANCE.tools.netRange);
      this.effects?.spawnTracer(this.aimOrigin, end);
      this.effects?.spawnImpact(end, 0x63f0d0, 1.4, 0.16);
      if (caught) {
        // Tell the player how to close the deal, but only when the drone is
        // actually close enough to reach: an out-of-reach hint would send
        // them across the room to press a key that does nothing.
        this.cameraRig.getHeadPosition(this.robot, scratchHeadPosition);
        const inReach =
          scratchHeadPosition.distanceTo(caught.position) <= BALANCE.combat.meleeRange;
        this.hud.notify(inReach ? 'NET CAPTURED - F TO FINISH' : 'NET CAPTURED', 1400);
        this.audio.play('hit', 0.4);
      } else {
        const reason =
          this.netGun.lastOutcome === 'cooldown'
            ? 'NET RECHARGING'
            : this.netGun.lastOutcome === 'out-of-range'
              ? `NET OUT OF RANGE (${BALANCE.tools.netRange} m)`
              : 'NET: NO TARGET IN CONE';
        this.hud.notify(reason, 1100);
        this.audio.play('ui', 0.25);
      }
      return caught !== null;
    }
    const fired = this.weapon.tryFire(
      this.aimOrigin,
      this.aimDirection,
      this.collision,
      this.drones.weaponTargets,
    );
    if (fired) {
      const surface = this.weapon.lastHitSurface;
      const point = this.weapon.lastHitPoint;
      if (surface && point && this.breachesHull(surface, point)) {
        // A direct hull hit always breaches. A shot that only reached the hull
        // by bouncing takes a chance, so a skipped round is not a death
        // sentence — but bank shots now make hull far easier to touch.
        if (
          hullBreachesOnRicochet(
            this.weapon.lastHitRicocheted,
            this.breachRng.next(),
            BALANCE.combat.ricochetBreachChance,
          )
        ) {
          this.hullBreach();
        }
      }
    }
    return fired;
  }

  /**
   * True when a shot at `point` on `surface` opens the hull.
   *
   * A face can be part exterior hull and part interior partition, so the spot
   * decides, not the face.
   */
  private breachesHull(surface: Surface, point: Vector3): boolean {
    const local = surface.toLocal(point, scratchLocal);
    return surface.hullAt(local.u, local.v);
  }

  /** Trigger the bullet-time dodge: warn, pull into first person, face the attacker. */
  private triggerBulletTime(): void {
    this.hud.notify('BULLET TIME', 1200);
    this.audio.play('alarm', 0.6);
    if (this.cameraRig.mode !== 'first') {
      this.bulletTimeForcedFirst = true;
      this.cameraRig.setMode('first');
      this.robotView.setFirstPerson(true);
      this.hud.setCameraMode('first');
    }
    // Turn the eye onto the attacker: the look is stored relative to the body,
    // so it is the direction from the head to where the shot came from that the
    // first-person camera has to be aimed along.
    this.bulletTime.getThreatPosition(scratchThreat);
    this.cameraRig.getHeadPosition(this.robot, scratchHeadPosition);
    scratchThreat.sub(scratchHeadPosition);
    if (scratchThreat.lengthSq() > 1e-8) {
      scratchThreat.normalize();
      const yaw = Math.atan2(
        scratchThreat.dot(this.robot.right),
        scratchThreat.dot(this.robot.forward),
      );
      const pitch = Math.asin(clamp(scratchThreat.dot(this.robot.up), -1, 1));
      this.cameraRig.easeLook(yaw, pitch, 0.18);
    }
  }

  /**
   * Throw the hook at the point under the crosshair, out of the gun the player
   * can see.
   *
   * Anchoring to the aim point rather than to the camera ray means the hook
   * goes where the reticle is, including straight at a magnetic drone, and in
   * third person it leaves the muzzle instead of an invisible point behind
   * the robot's head.
   */
  private fireGrapple(): boolean {
    this.computeAim();
    this.cameraRig.getMuzzlePosition(this.robot, scratchMuzzle);
    scratchPull.subVectors(this.aimPoint, scratchMuzzle);
    if (scratchPull.lengthSq() < 1e-8) scratchPull.copy(this.aimDirection);
    const fired = this.grapple.fire(scratchMuzzle, scratchPull.normalize());
    if (!fired) {
      // A dead right-click reads as a broken control, so say why: the state is
      // unchanged by a refused fire, which makes the reason unambiguous.
      const reason = this.grapple.status === 'idle' ? 'HOOK RECHARGING' : 'HOOK IS ALREADY OUT';
      this.hud.notify(reason, 1000);
      this.audio.play('ui', 0.2);
      return false;
    }
    this.drones.hearNoise(this.robot.position, BALANCE.stealth.shotNoiseRadius);
    return true;
  }

  /**
   * Reel the dog toward a wall anchor, or a magnetic drone toward the dog.
   *
   * A wall anchor does not only pull the body in mid air: standing on the
   * very surface the hook hit, the body lets go and lets the line haul it in,
   * which is what makes the tool a way *up* something rather than a way
   * across a gap. The hook lets go just short of the anchor so the magnet can
   * catch the body and play its landing, instead of dragging it through the
   * floor.
   */
  private applyGrapplePull(dt: number): void {
    if (!this.grapple.isAttached || dt <= 0) return;
    const drone = this.grapple.attachedDrone;
    if (drone) {
      // A destroyed drone is no longer a magnet, so the line goes slack
      // instead of hauling a wreck around.
      if (drone.isDestroyed) {
        this.grapple.release();
        return;
      }
      drone.pullToward(this.robot.position, BALANCE.tools.grapplePullSpeed * dt);
      // Let go once the drone is in front of the body rather than dragging it
      // inside: the hook is free to fire again, and the claw can finish what
      // the pull started.
      const reach = Math.max(
        BALANCE.tools.grappleReleaseDistance,
        drone.radius + BALANCE.player.bodyRadius,
      );
      if (drone.position.distanceTo(this.robot.position) <= reach) this.grapple.release();
      return;
    }
    scratchPull.subVectors(this.grapple.anchor, this.robot.position);
    const distance = scratchPull.length();
    if (distance < 1e-4) {
      this.grapple.release();
      return;
    }
    if (distance <= BALANCE.tools.grappleReleaseDistance) {
      this.grapple.release();
      return;
    }
    // Standing on the surface is not a reason to refuse the pull: the magnet
    // is what let go, so the body follows the line.
    if (this.robot.state !== 'FREE_FLIGHT') {
      this.robot.detachToFlight();
      this.hud.notify('REELING IN', 900);
    }
    const step = Math.min(distance, BALANCE.tools.grapplePullSpeed * dt);
    scratchPull.divideScalar(distance);
    this.robot.position.addScaledVector(scratchPull, step);
    // The line owns the motion now, so it also owns the velocity: the body
    // keeps moving along the line after the hook lets go.
    this.robot.velocity.copy(scratchPull).multiplyScalar(BALANCE.tools.grapplePullSpeed);
    this.collision.resolveObstacles(this.robot.position, BALANCE.player.bodyRadius);
    this.collision.room.pushInside(this.robot.position, 0.15);
  }

  /**
   * Close-range claw strike: the way a captured drone is finished off, and a
   * general shove for anything else in reach.
   */
  private tryMelee(): boolean {
    this.cameraRig.getHeadPosition(this.robot, scratchMuzzle);
    const drone = this.melee.tryStrike(scratchMuzzle, this.drones.list);
    // The swing is animated whether or not it connected: a miss should look
    // like a miss, not like a key that did nothing.
    this.playClawStrike(drone);
    if (!drone) {
      const reason =
        this.melee.lastOutcome === 'cooldown'
          ? 'CLAW RECHARGING'
          : `CLAW: NOTHING IN REACH (${BALANCE.combat.meleeRange} m)`;
      this.hud.notify(reason, 1100);
      this.audio.play('ui', 0.25);
      return false;
    }

    const finished = this.melee.lastOutcome === 'finished';
    // A claw on metal is loud: the strike gives the position away as much as
    // a shot does.
    this.drones.hearNoise(this.robot.position, BALANCE.stealth.shotNoiseRadius);
    // The strike itself, whether or not it is the last one: a finisher still
    // gets the drone's own explosion and score from the death handling, so
    // playing one here too would double the bang.
    this.effects?.spawnImpact(drone.position, 0xffe2a8, 2.4, 0.18);
    this.audio.play('hit', 0.6);
    this.hud.notify(finished ? 'DRONE FINISHED' : 'CLAW STRIKE', 1100);
    // The strike is also a pounce: the body lunges toward what it hit.
    scratchPull.subVectors(drone.position, this.robot.position);
    if (scratchPull.lengthSq() > 1e-8) {
      this.robot.velocity.addScaledVector(scratchPull.normalize(), BALANCE.combat.meleeLunge);
    }
    return true;
  }

  /**
   * Animate the claw swing toward whatever the strike was about.
   *
   * A refused strike (still recharging) does not animate: the claw is busy and
   * the notice already says so. Everything else swings, aimed at the drone it
   * hit or out along the aim on a miss, so the swipe always shows where the
   * blow was meant to land.
   *
   * In first person the body is hidden, so the view kick *is* the feedback.
   * That is why a miss kicks as well, just more softly.
   */
  private playClawStrike(drone: Drone | null): void {
    if (this.melee.lastOutcome === 'cooldown') return;

    scratchClawDirection.copy(drone ? drone.position : this.aimPoint).sub(this.robot.position);
    if (scratchClawDirection.lengthSq() < 1e-8) scratchClawDirection.copy(this.robot.forward);
    // The rig is drawn in the body's frame, so the aim has to be as well.
    scratchClawDirection.applyQuaternion(
      scratchClawInverse.copy(this.robot.bodyQuaternion).invert(),
    );
    this.robotView.clawStrike(scratchClawDirection);

    if (!this.settings.get('reducedMotion')) {
      this.cameraRig.addShake(drone ? 0.18 : 0.08);
    }
    if (drone) return;

    // A miss leaves its mark in the air at the claw's reach, so the swing lands
    // somewhere in the world and not only in the rig.
    scratchClawTip
      .copy(this.aimOrigin)
      .addScaledVector(this.aimDirection, BALANCE.combat.meleeRange * 0.8);
    this.effects?.spawnImpact(scratchClawTip, 0x9fb2c8, 1, 0.12);
  }

  /** Warn when the current aim points at an exterior hull wall. */
  private updateHullWarning(): void {
    const hit = this.collision.raycast(this.aimOrigin, this.aimDirection, BALANCE.combat.range);
    this.hud.setHullWarning(Boolean(hit?.surface && this.breachesHull(hit.surface, hit.point)));
  }

  /** Shooting an exterior wall opens the hull and ends the mission. */
  private hullBreach(): void {
    this.hud.notify('HULL BREACH', 4000);
    this.audio.play('explosion', 1);
    this.endRun('gameover');
  }

  private updatePlayerAudio(previous: LocomotionState, dt: number): void {
    const state = this.robot.state;

    if (state === 'LANDING' && previous !== 'LANDING') {
      this.stepDistance = 0;
      this.audio.play('magnet', 0.5);
      return;
    }

    if (state === 'SURFACE' && previous === 'LANDING') {
      this.audio.play('land', 0.7);
      const impact = Math.min(1, this.robot.landingImpactSpeed / BALANCE.landing.maxImpactSpeed);
      scratchPosition
        .copy(this.robot.position)
        .addScaledVector(this.robot.up, -BALANCE.player.attachHeight);
      this.effects?.spawnLandingDust(scratchPosition, this.robot.up, impact);
      if (!this.settings.get('reducedMotion')) {
        this.cameraRig.addShake(0.35 * impact);
      }
      // A landing is a loud metal clank. Sneaking softens it but does not make
      // it completely silent, so a careless drop can still give you away.
      const landingNoise =
        BALANCE.stealth.landingNoiseRadius * (this.robot.sneakAmount > 0.5 ? 0.35 : 1);
      this.emitNoise(landingNoise);
    }

    if (state === 'FREE_FLIGHT' && previous === 'SURFACE') {
      this.audio.play('jump', 0.5);
    }

    if (state === 'SURFACE') {
      const speed = this.robot.velocity.length();
      const moving = speed > 0.5;
      const sneaking = this.robot.sneakAmount > 0.5;
      if (moving && !this.moving) {
        this.stepDistance = 0;
        if (!sneaking) {
          this.audio.play('footstep', 0.35);
          this.emitNoise(BALANCE.stealth.footstepNoiseRadius);
        }
      } else {
        this.stepDistance += speed * dt;
        if (this.stepDistance >= BALANCE.player.stepStride) {
          this.stepDistance = 0;
          if (!sneaking) {
            this.audio.play('footstep', 0.4);
            this.emitNoise(BALANCE.stealth.footstepNoiseRadius);
          }
        }
      }
      this.moving = moving;
    } else {
      this.moving = false;
      this.stepDistance = 0;
    }
  }

  /** Send a footstep or landing noise to every drone close enough to hear it. */
  private emitNoise(radius: number): void {
    if (radius <= 0) return;
    this.robot.getFeetPosition(scratchNoise);
    this.drones.hearNoise(scratchNoise, radius);
  }

  /**
   * Show where the living drones are.
   *
   * The radar is the visual equivalent of the positional drone hum, so a
   * player who cannot hear the audio can still find an off-surface threat.
   */
  private updateRadar(): void {
    for (const drone of this.drones.list) {
      if (!drone.isDestroyed) {
        this.livingDronePositions.push(drone.position);
      }
    }
    this.cameraRig.camera.getWorldPosition(scratchPosition);
    this.cameraRig.getAimDirection(scratchForward);
    this.hud.setRadar(this.livingDronePositions, scratchPosition, scratchForward, this.robot.up);
    this.livingDronePositions.length = 0;
  }

  /**
   * Flash and sound once for each newly spawned bolt. The projectile list is
   * authoritative; a WeakSet keeps this cheap without retaining spent bolts.
   */
  private flashNewBolts(): void {
    for (const bolt of this.drones.bolts.active) {
      if (this.seenBolts.has(bolt)) continue;
      this.seenBolts.add(bolt);
      this.effects?.spawnImpact(bolt.origin, 0xff6a4a, 2.2, 0.12);
      this.audio.playAt('droneShot', bolt.origin, 0.55);
    }
  }

  private updateDroneState(): void {
    for (const drone of this.drones.list) {
      const wasDestroyed = this.destroyedDrones.has(drone.id);
      if (drone.isDestroyed && !wasDestroyed) {
        this.destroyedDrones.add(drone.id);
        this.audio.stopDroneHum(drone.id);
        this.score.add(BALANCE.combat.scorePerDrone);
        this.audio.playAt('explosion', drone.position, 0.9);
        this.effects?.spawnImpact(drone.position, 0xff7a4a, 3.2);
        // The objective has served its purpose once the first drone is down.
        if (this.destroyedDrones.size === 1) {
          this.hud.setObjective(
            `${this.drones.remaining} drone${this.drones.remaining === 1 ? '' : 's'} left`,
          );
        }
      }
    }
  }

  // ------------------------------------------------------------------- render

  /**
   * Draw the hook and its line from the muzzle the player can see to wherever
   * the hook currently is. Without this the grapple is an invisible state
   * change: nothing is thrown at the wall.
   */
  private updateGrappleView(): void {
    const view = this.grappleView;
    if (!view) return;
    if (this.grapple.status === 'idle') {
      view.hide();
      return;
    }
    this.cameraRig.getMuzzlePosition(this.robot, scratchMuzzle);
    view.show(scratchMuzzle, this.grapple.tip);
  }

  private render(frameDelta: number): void {
    const now = performance.now();
    const delta = Math.max(0.0001, (now - this.lastFrameTime) / 1000);
    this.lastFrameTime = now;
    this.fps = this.fps * 0.9 + (1 / delta) * 0.1;

    const speed = this.robot.velocity.length();
    this.robotView.update(
      frameDelta,
      this.robot.position,
      this.robot.bodyQuaternion,
      speed,
      this.robot.state === 'SURFACE',
      {
        yaw: this.cameraRig.headAimYaw,
        pitch: this.cameraRig.headAimPitch,
        roll: this.cameraRig.headAimRoll,
        // The head and the gun ride the viewer offset, so the gun stays put
        // relative to the view as the player leans: the tool goes where the
        // point of view goes.
        offset: this.cameraRig.getHeadRigOffset(this.robot, scratchHeadOffset),
      },
      this.cameraRig.recoilAmount,
      clamp(
        this.robot.turnRate * BALANCE.player.leanFactor,
        -BALANCE.player.maxLean,
        BALANCE.player.maxLean,
      ),
      this.robot.sneakAmount,
      this.robot.dodgeLean,
    );
    this.updateGrappleView();

    for (const drone of this.drones.list) {
      const view = this.droneViews.get(drone.id);
      if (drone.isDestroyed) {
        if (view) view.group.visible = false;
        continue;
      }
      if (view) {
        view.update(drone.position, this.elapsed, drone.isAlerted, drone.isNetted);
      }
      this.audio.updateDroneHum(drone.id, drone.position, this.phase === 'playing');
    }

    this.effects?.update(frameDelta);
    this.boltView.sync(this.drones.bolts.active);
    // While the focus window is open every bolt is drawn by the overlay (one
    // continuous representation, over the bars), so the world layer steps aside
    // to avoid drawing each bolt twice.
    this.boltView.group.visible = !this.bulletTimeOverlay.isEnabled;

    this.cameraRig.camera.getWorldPosition(scratchPosition);
    this.cameraRig.getAimDirection(scratchForward);
    this.audio.setListener(scratchPosition, scratchForward, this.robot.up);

    if (Debug.showOverlay) {
      this.debugOverlay.update([
        `fps        ${this.fps.toFixed(0)}`,
        `phase      ${this.phase}`,
        `state      ${this.robot.state}`,
        `surface    ${this.robot.surface?.id ?? '-'}`,
        `up         ${formatVector(this.robot.up)}`,
        `heading    ${((this.robot.heading * 180) / Math.PI).toFixed(0)} deg`,
        `position   ${formatVector(this.robot.position)}`,
        `velocity   ${speed.toFixed(2)} m/s`,
        `view       ${this.cameraRig.mode}`,
        `head track ${this.headTracker.status} ${this.headTracker.lastInferenceDurationMs.toFixed(1)} ms`,
        `gamepad    ${this.input.hasGamepad ? 'connected' : '-'}`,
        `input      ${this.describeInput()}`,
        `drones     ${this.drones.remaining}/${this.drones.list.length}`,
        `score      ${this.score.current}`,
      ]);
    }

    if (this.renderEnabled && !this.contextLost) {
      if (this.bulletTimeOverlay.isEnabled) {
        // World, then the focus frame, then the pop-out bolts over it. The
        // overlay pass turns auto-clear off by hand so the frame and bolts land
        // on top of the picture instead of replacing it.
        this.renderer.autoClear = false;
        this.renderer.clear();
        this.renderer.render(this.scene, this.cameraRig.camera);
        this.bulletTimeOverlay.render(this.renderer, this.cameraRig.camera);
        this.renderer.autoClear = true;
      } else {
        this.renderer.render(this.scene, this.cameraRig.camera);
      }
    }

    // The reticle is projected after the render, because the camera's world
    // matrix is only current once the renderer has used it.
    this.updateReticle();
  }

  /**
   * Place the world anchored reticle on the point the gun is aiming at.
   *
   * Both modes use it. The aim is the mouse axis, so a tracked lean moves the
   * viewpoint without moving the world point being aimed at: the reticle
   * slides to the window's principal point instead of staying pinned to the
   * screen centre, and it is hidden when that takes it off screen. With no
   * lean it sits exactly on the centre, where the fixed crosshair used to be.
   */
  private updateReticle(): void {
    scratchProjected.copy(this.aimPoint).project(this.cameraRig.camera);
    const onScreen =
      scratchProjected.z > -1 &&
      scratchProjected.z < 1 &&
      Math.abs(scratchProjected.x) <= 1 &&
      Math.abs(scratchProjected.y) <= 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const x = (scratchProjected.x * 0.5 + 0.5) * width;
    const y = (-scratchProjected.y * 0.5 + 0.5) * height;
    const distance = this.aimPoint.distanceTo(this.cameraRig.camera.position);
    this.hud.setReticle(x, y, distance, onScreen);
  }

  private describeInput(): string {
    const frame = this.lastInput;
    if (!frame) return '-';
    const pressed = [
      frame.jumpPressed ? 'jump' : '',
      frame.firePressed ? 'fire' : '',
      frame.grabHeld ? 'grab' : '',
      frame.crouchHeld ? 'crouch' : '',
    ]
      .filter(Boolean)
      .join('+');
    return `move ${frame.move.x.toFixed(1)},${frame.move.y.toFixed(1)} look ${frame.look.x.toFixed(0)},${frame.look.y.toFixed(0)}${pressed ? ` ${pressed}` : ''}`;
  }

  private resize(): void {
    this.resizeTo(window.innerWidth, window.innerHeight);
  }

  /** Resize the renderer and the camera to a viewport. */
  resizeTo(width: number, height: number): void {
    this.renderer.setSize(width, height, false);
    this.cameraRig.setAspect(width / Math.max(1, height));
  }

  /** QA hook: end the current round, used by the automated smoke test. */
  debugEndRun(result: 'gameover' | 'victory'): void {
    this.endRun(result);
  }

  /**
   * The QA surface for the automated smoke test.
   *
   * This is the supported way for a test to reach into the running game; see
   * `docs/TESTING.md` §4. It is deliberately read-mostly: the only mutators
   * are the ones a scripted session needs.
   */
  get qa(): {
    state: GameDebugState;
    robot: {
      health: number;
      alive: boolean;
      position: [number, number, number];
      velocity: [number, number, number];
    };
    drones: Array<{
      id: string;
      destroyed: boolean;
      position: [number, number, number];
      netted: boolean;
      magnetic: boolean;
    }>;
    droneViews: Array<{ id: string; position: [number, number, number]; visible: boolean }>;
    bolts: { active: number; drawn: number; worldVisible: boolean };
    robotView: {
      chassisVisible: boolean;
      neckVisible: boolean;
      headShellVisible: boolean;
      gunVisible: boolean;
      /** True while the claw strike animation is playing. */
      striking: boolean;
      /** Swings started so far. */
      strikes: number;
    };
    level: { id: string; name: string; surfaces: number; portals: number };
    headTracking: {
      x: number;
      y: number;
      forward: number;
      /** The mouse-owned head aim, in radians. The tracker never moves it. */
      aimYaw: number;
      aimPitch: number;
      /** The raw tracked pose, before scaling and inversion, for tests. */
      rawX: number;
      /** The sway range the last command used, metres. */
      swayRange: number;
    };
    bestScore: number | null;
    /** True while the view is held on the plane the body just stepped off. */
    planePinned: boolean;
    setCameraMode: (mode: 'third' | 'first') => void;
    /** Pull the plane swivel, as `Q` or a head tilt would. */
    swivelView: () => boolean;
    spawnTargetInFront: () => string | null;
    /** Put the robot back on its level spawn, without restarting the round. */
    resetToSpawn: () => void;
    fire: () => boolean;
    fireGrapple: () => boolean;
    /** Start a bullet-time window, as a threatening burst would. */
    triggerBulletTime: () => void;
    traceShot: () => {
      origin: [number, number, number];
      direction: [number, number, number];
      point: [number, number, number];
      targets: Array<{ id: string; position: [number, number, number] }>;
      hit: string | null;
      distance: number;
      hitWorld: boolean;
    };
    endRun: (result: 'gameover' | 'victory') => void;
    /** Run one simulation step and one render, without the animation loop. */
    step: (dt?: number) => void;
    /** Feed an action straight into the input layer. */
    inject: (action: InputAction, pressed: boolean) => void;
    /** The 3D room builder, as the scanner drives it. */
    build: {
      active: boolean;
      /** The scanner readout line. */
      scanner: string;
      /** How many pieces are placed in the map being edited. */
      placed: number;
      /** Where the aim ray meets the world, as the scanner sees it. */
      aim: () => [number, number, number] | null;
      /** Place the previewed piece, as the right mouse button does. */
      place: () => boolean;
      /** Delete whatever is at the aim point. */
      remove: () => boolean;
    };
    /** How many pop-out bolts are drawn over the focus frame. */
    popouts: () => number;
    /** The screen position (NDC) each pop-out clone lands on, for tests. */
    popoutScreens: () => Array<{ x: number; y: number }>;
    /** The touch interface, as the pad drives it. */
    touch: {
      active: boolean;
      visible: boolean;
      /** Set the virtual stick's analog axis, -1..1. */
      setMove: (x: number, y: number) => void;
      /** Accumulate a look drag in mouse pixels, as a thumb would. */
      look: (deltaX: number, deltaY: number) => void;
    };
    /** Aim, as a mouse movement would. */
    lookBy: (deltaX: number, deltaY: number) => void;
    /** Aim at an absolute yaw and pitch, in radians. */
    setLook: (yaw: number, pitch: number) => void;
    /** Apply a settings change, as the menu would. */
    applySettings: (patch: Partial<SettingsView>) => void;
    /** Start the head tracking feature, as the settings toggle would. */
    enableHeadTracking: () => Promise<void>;
    /** Resize the viewport, as the window resize handler would. */
    resize: (width: number, height: number) => void;
    /** Damage the player, as a drone bolt would. */
    damagePlayer: (amount: number) => void;
    /** Fire a bolt at the body from a world position, as a drone would. */
    fireBoltAt: (
      position: [number, number, number],
      speed?: number,
      target?: [number, number, number],
    ) => void;
    /** Pin a living drone at a world position, for a deterministic shot. */
    placeDroneAt: (id: string, position: [number, number, number]) => void;
    /** Destroy a drone by id, as a scripted shot would. */
    damageDrone: (id: string, amount: number) => void;
    /** The raw aiming frame, before the shot is resolved. */
    aimFrame: () => {
      camera: [number, number, number];
      cameraDirection: [number, number, number];
      head: [number, number, number];
      headDirection: [number, number, number];
      /** Where the head ray meets the level, clamped to the weapon range. */
      lookPoint: [number, number, number];
    };
  } {
    return {
      popouts: () => this.bulletTimeOverlay.activeCount,
      popoutScreens: () => {
        const camera = this.cameraRig.camera;
        camera.updateMatrixWorld();
        return this.boltPopVisuals.map((visual) => {
          const projected = visual.position.clone().project(camera);
          return { x: projected.x, y: projected.y };
        });
      },
      state: this.debugState,
      robot: {
        health: this.robot.hull,
        alive: this.robot.isAlive,
        position: [this.robot.position.x, this.robot.position.y, this.robot.position.z],
        velocity: [this.robot.velocity.x, this.robot.velocity.y, this.robot.velocity.z],
      },
      drones: this.drones.list.map((drone) => ({
        id: drone.id,
        destroyed: drone.isDestroyed,
        position: [drone.position.x, drone.position.y, drone.position.z],
        netted: drone.isNetted,
        magnetic: drone.magnetic,
      })),
      droneViews: Array.from(this.droneViews.entries()).map(([id, view]) => ({
        id,
        position: [view.group.position.x, view.group.position.y, view.group.position.z],
        visible: view.group.visible,
      })),
      bolts: {
        active: this.drones.bolts.active.length,
        drawn: this.boltView.activeCount,
        worldVisible: this.boltView.group.visible,
      },
      robotView: {
        chassisVisible: this.robotView.chassisVisible,
        neckVisible: this.robotView.neckVisible,
        headShellVisible: this.robotView.headShellVisible,
        gunVisible: this.robotView.gunVisible,
        striking: this.robotView.striking,
        strikes: this.robotView.strikes,
      },
      level: {
        id: this.built.definition.id,
        name: this.built.definition.name,
        surfaces: this.built.room.surfaces.size,
        portals: this.built.room.portals.length,
      },
      headTracking: {
        x: this.cameraRig.headViewOffset.x,
        y: this.cameraRig.headViewOffset.y,
        forward: this.cameraRig.headViewOffset.z,
        aimYaw: this.cameraRig.headAimYaw,
        aimPitch: this.cameraRig.headAimPitch,
        rawX: this.headTracker.pose.x,
        swayRange: this.robot.swayRange,
      },
      bestScore: this.debugBestScore,
      planePinned: this.cameraRig.isPlanePinned,
      swivelView: () => this.cameraRig.swivelToBodyPlane(),
      setCameraMode: (mode) => {
        this.cameraRig.setMode(mode, true);
        this.robotView.setFirstPerson(this.cameraRig.mode === 'first');
        this.hud.setCameraMode(this.cameraRig.mode);
      },
      spawnTargetInFront: () => this.spawnTargetInFront(),
      resetToSpawn: () => this.placePlayer(),
      fire: () => (this.phase === 'playing' ? this.fireCurrentWeapon() : false),
      fireGrapple: () => (this.phase === 'playing' ? this.fireGrapple() : false),
      triggerBulletTime: () => this.bulletTime.startDodge(),
      traceShot: () => this.traceShot(),
      endRun: (result) => this.endRun(result),
      step: (dt = BALANCE.simulation.step) => {
        this.update(dt);
        this.render(dt);
      },
      inject: (action, pressed) => this.input.inject(action, pressed),
      build: {
        active: this.buildMode,
        scanner: this.buildController.describe(),
        placed: this.buildController.placedCount,
        aim: () => {
          const hit = this.collision.raycast(this.aimOrigin, this.aimDirection, BUILD_REACH);
          return hit ? [hit.point.x, hit.point.y, hit.point.z] : null;
        },
        place: () => {
          const placement = this.buildController.preview(
            this.aimOrigin,
            this.aimDirection,
            BUILD_REACH,
            this.robot.position,
          );
          if (!placement || !this.buildController.place(placement)) return false;
          this.onBuildChanged(placement.kind === 'drone');
          return true;
        },
        remove: () => {
          const placement = this.buildController.preview(
            this.aimOrigin,
            this.aimDirection,
            BUILD_REACH,
            this.robot.position,
          );
          const point = placement
            ? new Vector3(...placement.position)
            : this.collision.raycast(this.aimOrigin, this.aimDirection, BUILD_REACH)?.point;
          return point ? this.buildController.removeAt(point) : false;
        },
      },
      touch: {
        active: this.touchActive(),
        visible: this.touchControls.visible,
        setMove: (x, y) => this.input.setTouchMove(x, y),
        look: (deltaX, deltaY) => this.input.addTouchLook(deltaX, deltaY),
      },
      lookBy: (deltaX, deltaY) => {
        this.cameraRig.applyLook(
          deltaX * (this.settings.get('invertLookX') ? -1 : 1),
          deltaY,
          this.settings.get('lookSensitivity'),
          this.settings.get('invertY'),
        );
      },
      setLook: (yaw, pitch) => this.cameraRig.setLook(yaw, pitch),
      applySettings: (patch) => this.applySettings(patch),
      enableHeadTracking: () => this.enableHeadTracking(),
      resize: (width, height) => this.resizeTo(width, height),
      damagePlayer: (amount) => {
        this.robot.takeDamage(amount);
        this.bus.emit('playerDamaged', { amount, health: this.robot.hull });
      },
      placeDroneAt: (id, position) => {
        const drone = this.drones.list.find((entry) => entry.id === id);
        if (!drone || drone.isDestroyed) return;
        drone.holdAt(new Vector3(position[0], position[1], position[2]));
      },
      damageDrone: (id, amount) => {
        const drone = this.drones.list.find((entry) => entry.id === id);
        if (!drone || drone.isDestroyed) return;
        drone.takeDamage(amount);
      },
      fireBoltAt: (position, speed = BALANCE.combat.boltSpeed, target) => {
        const from = new Vector3(position[0], position[1], position[2]);
        const aim = target ? new Vector3(target[0], target[1], target[2]) : this.robot.position;
        const direction = new Vector3().subVectors(aim, from);
        if (direction.lengthSq() < 1e-8) direction.copy(this.robot.forward);
        this.drones.bolts.spawn(
          from,
          direction,
          speed,
          BALANCE.combat.boltDamage,
          BALANCE.combat.boltLifetime,
        );
      },
      aimFrame: () => {
        this.cameraRig.camera.getWorldPosition(scratchCameraPosition);
        this.cameraRig.camera.getWorldDirection(scratchCameraForward);
        this.cameraRig.getHeadPosition(this.robot, scratchHeadPosition);
        this.cameraRig.getHeadAimDirection(this.robot, scratchHeadDirection);

        const hit = this.collision.raycast(
          scratchHeadPosition,
          scratchHeadDirection,
          BALANCE.combat.range,
        );
        const lookPoint = hit
          ? hit.point
          : scratchHeadPosition.clone().addScaledVector(scratchHeadDirection, BALANCE.combat.range);

        return {
          camera: [scratchCameraPosition.x, scratchCameraPosition.y, scratchCameraPosition.z],
          cameraDirection: [scratchCameraForward.x, scratchCameraForward.y, scratchCameraForward.z],
          head: [scratchHeadPosition.x, scratchHeadPosition.y, scratchHeadPosition.z],
          headDirection: [scratchHeadDirection.x, scratchHeadDirection.y, scratchHeadDirection.z],
          lookPoint: [lookPoint.x, lookPoint.y, lookPoint.z],
        };
      },
    };
  }

  /**
   * QA hook: report what the next shot would hit, without firing.
   */
  private traceShot(): {
    origin: [number, number, number];
    direction: [number, number, number];
    point: [number, number, number];
    targets: Array<{ id: string; position: [number, number, number] }>;
    hit: string | null;
    distance: number;
    hitWorld: boolean;
  } {
    this.computeAim();
    const targets = this.drones.weaponTargets;
    const result = hitscan(
      this.aimOrigin,
      this.aimDirection,
      BALANCE.combat.range,
      this.collision,
      targets,
    );
    return {
      origin: [this.aimOrigin.x, this.aimOrigin.y, this.aimOrigin.z],
      direction: [this.aimDirection.x, this.aimDirection.y, this.aimDirection.z],
      point: [this.aimPoint.x, this.aimPoint.y, this.aimPoint.z],
      targets: targets.map((target) => ({
        id: target.id,
        position: [target.position.x, target.position.y, target.position.z],
      })),
      hit: result.target?.id ?? null,
      distance: result.distance,
      hitWorld: result.hitWorld,
    };
  }

  /** QA hook: put a live drone directly in front of the gun. */
  private spawnTargetInFront(): string | null {
    const drone = this.drones.list.find((entry) => !entry.isDestroyed);
    if (!drone) return null;
    this.computeAim();

    // Two metres short of the aim point, so the shot has a clear line and
    // the drone is not inside the wall. Against a near wall, stay inside the
    // ray: half the distance is always short of the surface.
    const distance = this.aimOrigin.distanceTo(this.aimPoint);
    const standoff = Math.min(2, distance * 0.5);
    const target = this.aimPoint.clone().addScaledVector(this.aimDirection, -standoff);
    drone.holdAt(target);
    return drone.id;
  }

  /** QA hook: the score that would be recorded. */
  get debugBestScore(): number | null {
    return this.highScores.best?.score ?? null;
  }

  /** Exposed for the automated smoke test. */
  get debugState(): GameDebugState {
    return {
      phase: this.phase,
      robotState: this.robot.state,
      surface: this.robot.surface?.id ?? '-',
      droneCount: this.drones.list.length,
      score: this.score.current,
      fps: Math.round(this.fps),
      position: [this.robot.position.x, this.robot.position.y, this.robot.position.z],
      velocity: this.robot.velocity.length(),
      cameraMode: this.cameraRig.mode,
      weapon: this.weaponMode,
      bulletTime: this.bulletTime.active,
      grapple: this.grapple.status,
      audio: this.audio.debugState,
    };
  }
}

function formatVector(vector: Vector3): string {
  return `${vector.x.toFixed(1)}, ${vector.y.toFixed(1)}, ${vector.z.toFixed(1)}`;
}

/**
 * The live viewer offset for the camera test, in centimetres.
 *
 * This is the number the off-axis window is built from: it moves when the head
 * moves, before any of the projection work, so a player can tell a working
 * setup from a dead one at a glance.
 */
function formatViewerOffset(offset: Vector3): string {
  const cm = (value: number): string =>
    `${value < 0 ? '-' : '+'}${Math.abs(value * 100).toFixed(1)}`;
  return `VIEWER x ${cm(offset.x)} · y ${cm(offset.y)} · z ${cm(offset.z)} cm`;
}
